import React, { useState, useEffect, useRef, useMemo } from "react";
import { ref, update, set, onValue } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { database, firestore, functions } from '../../firebaseConfig';
import { doc, updateDoc, getDoc, onSnapshot, deleteDoc, deleteField } from 'firebase/firestore';
import EmailAiWebPreview from '../EmailAiWebPreview';
import { addFooterToEmail, convertToHtml } from '../EmailFooterGenerator';
import { resolveFooterForSend } from './resolveFooterForSend';
import { effectiveContactStatus, topicForPreparedDraft } from './contactStage';
import EmailAiPopupTopics from '../EmailAiPopupTopics';
import ScreenComposerPopup from '../ScreenComposerPopup';
import ScreenComposerNotifier from '../ScreenComposerNotifier';
import useScreenComposerBackground from '../useScreenComposerBackground';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../../utils/cloudFunctions';
import { emailGenerationCost, costOf } from '../../config/creditPlans';
import { chargeCredits } from '../../utils/creditCharge';
import useCreditGate from '../../hooks/useCreditGate';
import { advanceFlowOnEmailSend } from '../warmupFlowUtils';
import { completeTask, CONTACT_PATCH_EVENT } from '../tasks/taskUtils';
import { resolveFlowSenderEmail } from '../flowSenderUtils';
import { minimalSignalsSnapshot } from '../leadSignalEngine';
import { 
  selectRandomWithTracking, 
  selectRandomWithRotation,
  selectInstructionWithRotation,
  greetingToneFromVariant,
  getEffectivePacing,
  extractItemId, 
  extractItemText,
  findTopicInUserTopics,
  trackEmailSend,
  coupleScoreToAnalytics,
  hasInstructionBlocks,
  stripInstructionBlocks,
} from './emailTrackingUtils';
import { useActiveWorld } from '../../hooks/useActiveWorld';
import useCustomFields from '../../hooks/useCustomFields';
import emailPersonalities from '../emailPersonalities';
import { SALESGOD_PERSONAS } from '../../shared/salesgodPersonas';
import { logContactActivity, logStatusChange } from '../../shared/contactActivityLog';
import { getAllContactEmails, getDecisionMakersWithoutEmail, getFirstDmName, getPrimaryDecisionMaker, getActiveTemplatePref, getActiveDmFallback, getRecipientInfoForContact, pickOutboundRecipient } from '../../shared/enums';
import { resolveFollowUpSubject, SUBJECT_REPLY_LINE_ID } from '../../utils/followUpSubject';
import EmailAttachmentBar from '../../components/EmailAttachmentBar';
import EmailHistoryPanel from './EmailHistoryPanel';
import useSentRecipients from './useSentRecipients';
import useUndeliverableEmails from './useUndeliverableEmails';
import FollowUpHistoryWarning from './FollowUpHistoryWarning';
import CallContextChip from './CallContextChip';
import LiveSpamBadge from './LiveSpamBadge';
import useSpamCheckMode from './useSpamCheckMode';
import { requestAiSpamCheck } from './requestAiSpamCheck';
import ConfirmModal from '../ConfirmModal';
import SendConfirmModal from './SendConfirmModal';
import ResearchInfoTabs from './ResearchInfoTabs';
import { fetchPreviousSentEmail, isFollowUpTopicType } from './previousEmailContext';
import { duplicateFollowUpSends } from './duplicateFollowUpSend';
import RichEmailEditor from './RichEmailEditor';
import { collectAiImageTokens, generateAiImageForContact, withResolvedAiImages, withResolvedImageAltText, buildImagePromptVariables } from './dynamicImageUtils';
// Voice-Clone bridge — used to detect and route Voice-Clone-mode templates
// to the new backend pipeline. Standard-mode templates fall through the
// existing code path and are unaffected.
import {
  isVoiceCloneTopicData,
  buildVoiceClonePayload,
  buildCloneWriterBrief,
  shouldGenerateSmartSubject,
  buildVoiceCloneSmartSubjectId,
  loadFreshContactRow,
} from '../CloneTemplate/buildVoiceClonePayload';
import { readVoiceCloneError, buildVoiceCloneError } from '../CloneTemplate/voiceCloneErrors';
import { accountTextSignature } from '../accountTextSignature';
import { useCopyClones } from '../CopyCloneAI/useCopyClones';
import { autoCloneName } from '../CopyCloneAI/cloneUtils';
import { formatReviewAuditContext } from '../../shared/reviewAuditContext';
import { formatConnectorContext } from '../../shared/connectorContext';

const WRITER_CATEGORY_ICONS = {
  icebreaker: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v8l4-4"/><path d="M12 10l-4-4"/><path d="M20 16.2A4.5 4.5 0 0017.5 8h-1.8A7 7 0 104 14.9"/><path d="M8 19h8"/><path d="M10 22h4"/></svg>,
  lossframing: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  problemformula: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/><line x1="11" y1="8" x2="11" y2="14"/></svg>,
  proofstage: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>,
  contentcta: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
};
const WRITER_CATEGORIES = [
  { id: 'icebreaker', label: 'Icebreaker' },
  { id: 'lossframing', label: 'Loss Framing' },
  { id: 'problemformula', label: 'Diagnosis' },
  { id: 'proofstage', label: 'Proof' },
  { id: 'contentcta', label: 'Content CTA' },
];

// Lightweight stable hash of the Writer's input context. When this changes (email body
// or context edited), the cached digest is invalidated so the backend rebuilds it.
const hashWriterContext = (...parts) => {
  const str = parts.filter(Boolean).join('||');
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return String(h);
};

// Helper-Funktion für URL-Formatierung
const formatURL = (url, prefix) => {
  if (!url) return '';
  
  // Wenn URL bereits http:// oder https:// enthält
  if (url.match(/^https?:\/\//i)) {
    return url;
  }
  
  // Entferne @ von Instagram-Handles
  if (prefix === 'https://instagram.com/') {
    url = url.replace('@', '');
  }
  
  return `${prefix}${url}`;
};

const NormalModeEmail = ({ 
  onClose, 
  rowData, 
  userId, 
  isReply, 
  source, 
  onEmailResult,
  userSettings,
  aiSettings,
  userTopics,
  isTopicsLoaded,
  senderEmails,
  topicTypeMapping
}) => {
  const { currentUserId } = useActiveWorld();
  const { contextFields } = useCustomFields(userId);
  // A mail parked in SafeGuard, opened to go out as it was written: no
  // generation, its own subject/body/recipient/sender.
  const resendDraft = rowData?.resendDraft || null;
  
  const [selectedTopic, setSelectedTopic] = useState("");
  // Style removed — defined in template
  const [contextInfo, setContextInfo] = useState("");
  const [chanceRadarText, setChanceRadarText] = useState("");
  const [subjectLine, setSubjectLine] = useState(resendDraft?.subject || "");
  const [bodyContent, setBodyContent] = useState(resendDraft?.body || "");
  // Per-recipient dynamic AI images: token -> { status, imageUrl, altText, maxWidth, align, name, error }
  const [aiImages, setAiImages] = useState({});
  // Tokens the user manually cancelled/removed. A late-resolving generation for
  // such a token must be ignored so it can never resurrect a removed image.
  const removedImageTokensRef = useRef(new Set());
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const followUpRecipientTouchedRef = useRef(false);
  const followUpContactRef = useRef('');
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [tempEmails, setTempEmails] = useState([]);
  
  // Email Tracking States - store IDs of selected template, subject line, and instruction.
  // `variantId` is set ONLY for Voice-Clone-mode sends; the backend uses it
  // to credit per-structure-variant stats (parallel to subjectLineStats /
  // instructionStats). Standard-mode sends leave it null and the backend
  // simply doesn't update variantStats.
  const [trackingData, setTrackingData] = useState({
    templateId: null,
    templateName: null,
    subjectLineId: null,
    instructionId: null,
    variantId: null,
  });
  const [isStatusSet, setIsStatusSet] = useState(true);
  const { requireCredits, openCreditsModal } = useCreditGate();
  const [isGenerating, setIsGenerating] = useState(!resendDraft);
  const [isPerfecting, setIsPerfecting] = useState(false);
  const [perfectSliderOpen, setPerfectSliderOpen] = useState(false);
  const [perfectCreativity, setPerfectCreativity] = useState(3);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [writingSparks, setWritingSparks] = useState([]);
  const [sparksLoading, setSparksLoading] = useState(false);
  const [sparksLoadingPos, setSparksLoadingPos] = useState(null);
  const [sparksCopied, setSparksCopied] = useState(null);
  const [writerDirections, setWriterDirections] = useState([]);
  const [activeDirection, setActiveDirection] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [sidePanelShowMore, setSidePanelShowMore] = useState(false);
  const [cachedBriefing, setCachedBriefing] = useState(null);
  const [expandingIdx, setExpandingIdx] = useState(null);
  const [sparksGenCount, setSparksGenCount] = useState(0);
  const [activePersona, setActivePersona] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const [frameworkPickerOpen, setFrameworkPickerOpen] = useState(false);
  const [expandedPersonality, setExpandedPersonality] = useState(null);
  const [regenCustomNote, setRegenCustomNote] = useState('');
  // Quick CopyTwin Re-Generate popover (lives next to the 3-dots menu).
  const [cloneQuickOpen, setCloneQuickOpen] = useState(false);
  const regenQuickRef = useRef(null);
  // All trained CopyTwins in this workspace — used so a Re-Generate on a
  // CopyTwin template can switch to an alternative clone for a single send.
  const { clones: copyClones } = useCopyClones(userId);
  const [ccEmails, setCcEmails] = useState(() => resendDraft?.cc || []);
  const [selectedSenderEmail, setSelectedSenderEmail] = useState("");
  const [randomSenderMode, setRandomSenderMode] = useState(false);
  const [showBlockingOverlay, setShowBlockingOverlay] = useState(false);
  const [showScreenComposer, setShowScreenComposer] = useState(false);
  const [screenComposerImage, setScreenComposerImage] = useState(null);
  const [attachments, setAttachments] = useState(() => resendDraft?.attachments || []);
  const attachmentInputRef = useRef(null);
  const [subjectAltsOpen, setSubjectAltsOpen] = useState(false);
  const [subjectAlts, setSubjectAlts] = useState([]);
  const [subjectAltsLoading, setSubjectAltsLoading] = useState(false);
  // True when subjectAlts came pre-baked from the last worldEmailCreator
  // response, so the ⚡ button opens the dropdown instantly on first click
  // (no round-trip). Cleared on first open so the second click re-fetches.
  const [subjectAltsPrecomputed, setSubjectAltsPrecomputed] = useState(false);
  const [checkingDmEmails, setCheckingDmEmails] = useState({});
  const [dmEmailOverrides, setDmEmailOverrides] = useState({});
  const [inlineAddDm, setInlineAddDm] = useState(null);
  const [savingDm, setSavingDm] = useState(false);
  const [localDmAdditions, setLocalDmAdditions] = useState([]);

  const scBackground = useScreenComposerBackground(userId, (imageData) => {
    setScreenComposerImage(imageData);
  });

  // Ref für automatische Generierung
  const hasAutoGenerated = useRef(!!resendDraft);
  const sparksLockRef = useRef(false);
  const expandLockRef = useRef(false);
  // SalesGod Writer digest cache + angle ledger.
  // digestHashRef = content hash the cached digest was built for; when the email/context
  // changes, the hash changes and we force a fresh digest. usedAnglesRef = ids of angles
  // already shown to the user, so each new generation picks FRESH angles.
  const usedAnglesRef = useRef([]);
  const digestHashRef = useRef(null);
  const subjectInputRef = useRef(null);
  const actionMenuRef = useRef(null);
  const lastInstructionIdRef = useRef(null);
  const subjectTemplateRef = useRef(null);

  const effectiveRowData = React.useMemo(() => {
    let base = rowData;
    // Merge locally added decision makers into the rowData view
    if (localDmAdditions.length > 0) {
      let dms = [];
      try {
        const raw = rowData?.lead_decision_makers;
        if (raw && typeof raw === 'string') dms = JSON.parse(raw);
        else if (Array.isArray(raw)) dms = raw;
      } catch (_) {}
      base = { ...rowData, lead_decision_makers: JSON.stringify([...dms, ...localDmAdditions]) };
    }
    if (!base?.lead_decision_makers || Object.keys(dmEmailOverrides).length === 0) return base;
    try {
      const parsed = typeof base.lead_decision_makers === 'string'
        ? JSON.parse(base.lead_decision_makers)
        : base.lead_decision_makers;
      if (!Array.isArray(parsed)) return base;
      const nextDms = parsed.map((dm) => {
        const key = (dm?.email || '').trim().toLowerCase();
        const override = key ? dmEmailOverrides[key] : null;
        if (!override) return dm;
        if (override.removed) {
          return { ...dm, email: '', emailChecked: true, emailValidationStatus: 'invalid', emailValidationFailed: true, emailVerified: false };
        }
        if (override.catchAll) {
          return { ...dm, emailChecked: true, emailValidationStatus: 'catch_all', emailValidationFailed: false, emailVerified: false, emailNote: 'catch-all domain — unverified, may bounce' };
        }
        return { ...dm, emailChecked: true, emailValidationStatus: 'valid', emailValidationFailed: false, emailVerified: true };
      });
      return { ...base, lead_decision_makers: JSON.stringify(nextDms) };
    } catch (_) {
      return base;
    }
  }, [rowData, dmEmailOverrides, localDmAdditions]);

  // All available emails (general + decision maker)
  const allEmails = React.useMemo(() => getAllContactEmails(effectiveRowData), [effectiveRowData]);
  const emailAddresses = React.useMemo(() => allEmails.filter(e => !e.emailMissing).map(e => e.email), [allEmails]);

  // Addresses that hard-bounced → undeliverable (struck through, not selectable,
  // send-blocked). Driven primarily by the live undeliverableEmails index (the
  // same source the server-side send gates enforce), so it is robust regardless
  // of whether the bounce could be matched back onto this contact. The contact's
  // own emailBounceCheck is a fallback for the World DB strikethrough.
  const { undeliverableSet, bounceTypeOf } = useUndeliverableEmails(userId);
  const undeliverableEmail = React.useMemo(() => {
    const bc = effectiveRowData?.emailBounceCheck;
    return (bc && bc.action === 'undeliverable' && bc.originalEmail)
      ? String(bc.originalEmail).toLowerCase() : null;
  }, [effectiveRowData]);
  const isEmailUndeliverable = React.useCallback((email) => {
    if (!email) return false;
    const lower = String(email).toLowerCase();
    if (undeliverableSet.has(lower)) return true;
    return !!undeliverableEmail && lower === undeliverableEmail;
  }, [undeliverableSet, undeliverableEmail]);

  // Addresses we already sent an intro/follow-up to → flag red in the To-field
  // and gate re-sends behind an extra confirmation (see handleSend).
  const { isContacted: isAddressContacted, sentCount, lastSent, sentMessages, priorSends, loading: sentHistoryLoading } = useSentRecipients(userId, rowData?.id, emailAddresses);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const resendConfirmedRef = useRef(false);
  // Follow-up-Sanity-Guard: separater Confirm-Dialog vor dem Send, sobald
  // eine Konversation zu diesem Kontakt existiert (auch bei Follow-up-Steps).
  const [followUpConfirmOpen, setFollowUpConfirmOpen] = useState(false);
  const followUpConfirmedRef = useRef(false);

  // Live spam-risk badge. Rule-based by default; the "Intelligent Spam Analyzer"
  // (AI) mode runs a debounced cloud check that catches up after generation and
  // after edits. While the AI request is in-flight the badge falls back to the
  // instant local rule check, so the warning always matches the current text.
  const spamCheckMode = useSpamCheckMode(userId);
  const [aiSpam, setAiSpam] = useState(null);
  const [aiSpamPending, setAiSpamPending] = useState(false);
  const aiSpamReqId = useRef(0);
  const aiSpamAnalyzedRef = useRef(null);

  // Show the follow-up history warning only while composing a follow-up AND a
  // prior conversation already exists. Also drives spam-check context: on a
  // follow-up a "Re:/Fwd:" subject prefix is legitimate, not a fake-reply trick.
  const isFollowUpStep = isFollowUpTopicType(topicTypeMapping[selectedTopic]);

  useEffect(() => {
    if (spamCheckMode !== 'ai') { setAiSpam(null); setAiSpamPending(false); return undefined; }
    const hasContent = (bodyContent && bodyContent.trim()) || (subjectLine && subjectLine.trim());
    if (!hasContent) { setAiSpam(null); setAiSpamPending(false); aiSpamAnalyzedRef.current = null; return undefined; }
    const combined = `${subjectLine || ''}\u0000${bodyContent || ''}`;
    // Already analyzed this exact text (fresh result or cache hit) → no re-call.
    if (aiSpam && aiSpamAnalyzedRef.current === combined) { setAiSpamPending(false); return undefined; }
    const id = ++aiSpamReqId.current;
    setAiSpamPending(true);
    const timer = setTimeout(async () => {
      const result = await requestAiSpamCheck({ subject: subjectLine, body: bodyContent, isFollowUp: isFollowUpStep, userId });
      if (id !== aiSpamReqId.current) return;
      aiSpamAnalyzedRef.current = combined;
      setAiSpam(result);
      setAiSpamPending(false);
    }, 1400);
    return () => clearTimeout(timer);
  }, [bodyContent, subjectLine, spamCheckMode, aiSpam, isFollowUpStep, userId]);

  // A previously-contacted address is only a "duplicate send" to guard against
  // when composing a first-touch mail (intro). In a follow-up step, mailing an
  // address we already introduced ourselves to is the entire point — so we must
  // NOT flag it red or gate it behind the resend confirm there.
  const isResendBlocked = (email) => !isFollowUpStep && isAddressContacted(email);

  // Same rule as bulk mode: a first follow-up after the intro is quiet.
  // Confirmation only when this stage or this template already went to the address.
  const followUpDuplicates = (isFollowUpStep && !sentHistoryLoading)
    ? duplicateFollowUpSends(priorSends, {
        email: selectedEmail,
        stage: topicTypeMapping?.[selectedTopic],
        topicName: selectedTopic,
        templateId: trackingData?.templateId || null,
      })
    : [];

  const activeTemplatePref = React.useMemo(
    () => getActiveTemplatePref(selectedTopic, userTopics, topicTypeMapping),
    [selectedTopic, userTopics]
  );
  const selectedTopicData = React.useMemo(
    () => findTopicInUserTopics(userTopics, selectedTopic),
    [selectedTopic, userTopics]
  );
  const activeDmFallback = React.useMemo(
    () => getActiveDmFallback(selectedTopic, userTopics, topicTypeMapping),
    [selectedTopic, userTopics]
  );

  // One decision for the To-field. A resend or a task address wins. Otherwise
  // a follow-up continues the last real send, including when that address
  // lives only in the sent history.
  useEffect(() => {
    const contactId = rowData?.id || '';
    if (followUpContactRef.current !== contactId) {
      followUpContactRef.current = contactId;
      followUpRecipientTouchedRef.current = false;
    }
    if (followUpRecipientTouchedRef.current) return;
    if (resendDraft?.recipient) {
      setSelectedEmail(resendDraft.recipient);
      return;
    }
    if (rowData?.taskRecipient) {
      setSelectedEmail(rowData.taskRecipient);
      return;
    }
    const picked = pickOutboundRecipient({
      contact: effectiveRowData,
      emailPreference: activeTemplatePref || 'auto',
      dmFallback: activeDmFallback,
      isFollowUp: isFollowUpStep,
      lastSentTo: lastSent?.to,
      historyReady: !sentHistoryLoading,
    });
    const deliverable = allEmails.filter(e => e.email && !isEmailUndeliverable(e.email));
    const match = picked
      ? deliverable.find(e => e.email.toLowerCase() === picked.toLowerCase())
      : null;
    const next = match
      ? match.email
      : ((picked || !isFollowUpStep || !sentHistoryLoading) ? (deliverable[0]?.email || '') : '');
    if (!next) return;
    setSelectedEmail(cur => (cur && cur.toLowerCase() === next.toLowerCase() ? cur : next));
  }, [allEmails, effectiveRowData, rowData?.id, rowData?.taskRecipient, activeTemplatePref, activeDmFallback, isEmailUndeliverable, resendDraft, isFollowUpStep, lastSent, sentHistoryLoading]);

  useEffect(() => {
    setDmEmailOverrides({});
    setCheckingDmEmails({});
  }, [rowData?.id]);

  // Kontextinfo aus rowData setzen (includes outreach notes + DM context)
  useEffect(() => {
    if (rowData) {
      const outreachNotes = (rowData.lead_notes || [])
        .filter(n => n.type === 'outreach')
        .map(n => n.text)
        .join('\n');
      const igInsight = rowData.researchIntelligence?.instagramInsight;
      const igPosts = rowData.researchIntelligence?.instagramPosts;
      const igParts = [];
      if (igInsight) {
        igParts.push(`[IG RESEARCH] Instagram (@${igInsight.handle}): ${igInsight.summary}`);
        if (igInsight.uniqueFacts?.length) igParts.push(`Key facts: ${igInsight.uniqueFacts.join('; ')}`);
        if (igInsight.topics?.length) igParts.push(`Topics: ${igInsight.topics.join(', ')}`);
        if (igInsight.targetAudience) igParts.push(`Their audience: ${igInsight.targetAudience}`);
        if (igInsight.painPoints?.length) igParts.push(`Pain points: ${igInsight.painPoints.join('; ')}`);
        if (igInsight.sellingPoints?.length) igParts.push(`Their USPs: ${igInsight.sellingPoints.join('; ')}`);
        if (igInsight.salesAngle) igParts.push(`Recommended angle: ${igInsight.salesAngle}`);
        if (igInsight.activityScore != null) igParts.push(`Activity: ${igInsight.activityScore}/10, Engagement: ${igInsight.engagementScore ?? '?'}/10${igInsight.engagementTrend && igInsight.engagementTrend !== 'stable' ? ` (${igInsight.engagementTrend})` : ''}`);
        if (igInsight.isDeadAccount) igParts.push(`WARNING: Dead account — no recent posts and zero engagement.`);
        else if (igInsight.isActive === false) igParts.push(`Note: Inactive on Instagram (${igInsight.daysSinceLastPost}d since last post)`);
        if (Array.isArray(igPosts) && igPosts.length > 0) {
          const recentPosts = igPosts
            .filter(p => p.text)
            .slice(0, 3)
            .map((p, i) => {
              const excerpt = p.text.length > 120 ? p.text.substring(0, 120) + '...' : p.text;
              const stats = [p.like_count ? `${p.like_count} likes` : '', p.comment_count ? `${p.comment_count} comments` : ''].filter(Boolean).join(', ');
              return `  ${i + 1}. "${excerpt}"${stats ? ` (${stats})` : ''}`;
            });
          if (recentPosts.length > 0) igParts.push(`Recent posts:\n${recentPosts.join('\n')}`);
        }
      }
      const igContext = igParts.length > 0 ? '\n' + igParts.join('\n') : '';
      const cfLines = (contextFields || [])
        .map((cf) => {
          const val = rowData[cf.key];
          return val ? `${cf.label}: ${val}` : '';
        })
        .filter(Boolean)
        .join('\n');

      const selectedDm = allEmails.find(e => e.email === selectedEmail && e.source === 'decision_maker');
      const dmContext = selectedDm
        ? `\nRecipient: ${selectedDm.dmName}${selectedDm.dmRole ? ` (${selectedDm.dmRole})` : ''} — address this person by name`
        : '';

      const seo = rowData.researchIntelligence?.seoAnalysis;
      const seoParts = [];
      if (seo && seo.data) {
        const core = seo.data.core;
        const vis = core?.visibility?.value;
        const kwTotal = core?.keywordsTotal;
        const kwTop10 = core?.keywordsTop10;
        const headline = [];
        if (vis != null) headline.push(`Visibility ${Number(vis).toFixed(2)}`);
        if (kwTotal != null) headline.push(`${kwTotal} keywords total`);
        if (kwTop10 != null) headline.push(`${kwTop10} in Top 10`);
        if (headline.length) seoParts.push(`[SEO ANALYSIS] ${headline.join(' · ')}${seo.country ? ` (Google ${seo.country.toUpperCase()})` : ''}`);

        const tv = seo.data.trafficValue;
        if (tv?.estimatedTrafficMonthly != null) {
          seoParts.push(`Estimated organic traffic: ~${tv.estimatedTrafficMonthly.toLocaleString('de-DE')} visitors/month${tv.estimatedValueMonthly ? ` worth ~€${tv.estimatedValueMonthly.toLocaleString('de-DE')}/month` : ''}`);
        }

        const vt = seo.data.visibilityTrend;
        if (vt?.changePct != null) {
          const oldest = Array.isArray(vt.points) && vt.points.length ? vt.points[vt.points.length - 1] : null;
          seoParts.push(`Visibility trend: ${vt.direction === 'up' ? 'GAINING' : vt.direction === 'down' ? 'LOSING' : 'flat'} ${vt.changePct > 0 ? '+' : ''}${vt.changePct}% over ${vt.overWindow || '12M'}${oldest && vt.current != null ? ` (${oldest.value} → ${vt.current})` : ''}`);
        }

        const bl = seo.data.backlinks;
        if (bl?.total != null) seoParts.push(`Backlinks: ${bl.total}${bl.referringDomains != null ? ` from ${bl.referringDomains} domains` : ''}`);

        const comps = Array.isArray(seo.data.competitors) ? seo.data.competitors.slice(0, 3) : [];
        if (comps.length) {
          seoParts.push(`Top competitors: ${comps.map(c => `${c.domain}${c.match != null ? ` (${Number(c.match).toFixed(2)})` : ''}`).join(', ')}`);
        }

        const opps = Array.isArray(seo.data.opportunities) ? seo.data.opportunities.slice(0, 3) : [];
        if (opps.length) {
          seoParts.push(`Keyword opportunities (positions 4-10, quick wins): ${opps.map(o => `"${o.keyword}" (pos ${o.position})`).join(', ')}`);
        }

        const rd = seo.data.rankingDistribution;
        if (rd?.page01 != null) seoParts.push(`Ranking distribution — Page 1: ${rd.page01}, Page 2: ${rd.page02 || 0}, Page 3: ${rd.page03 || 0}`);
      }
      const seoContext = seoParts.length > 0 ? '\n' + seoParts.join('\n') : '';

      const wa = rowData.websiteAnalysis;
      const waParts = [];
      if (wa) {
        if (wa.cms) waParts.push(`[WEBSITE ANALYSIS] CMS: ${wa.cms}${wa.cms_confidence ? ` (${Math.round(wa.cms_confidence * 100)}% confidence)` : ''}${wa.version_estimate ? `, Version: ${wa.version_estimate}` : ''}`);
        const waProductCount = wa.product_count ?? wa.productCount;
        if (waProductCount != null) waParts.push(`Product count: ~${waProductCount}${wa.category_count ? `, ${wa.category_count} categories` : ''}`);
        if (wa.website_age) waParts.push(`Website age: ${wa.website_age.label || ''} (score ${wa.website_age.score}/100, era: ${wa.website_age.estimated_era || ''})`);
        else if (wa.websiteAge) waParts.push(`Website age: ${wa.websiteAge.label || ''} (score ${wa.websiteAge.score}/100, era: ${wa.websiteAge.estimatedEra || wa.websiteAge.estimated_era || ''})`);
        if (wa.industry) waParts.push(`Industry: ${wa.industry}`);
        if (wa.recommended_sales_angle) waParts.push(`Recommended approach: ${wa.recommended_sales_angle}`);
        if (wa.tech_stack_maturity) waParts.push(`Tech maturity: ${wa.tech_stack_maturity}`);
      }
      const waContext = waParts.length > 0 ? '\n' + waParts.join('\n') : '';

      let descriptionClean = rowData.lead_description || '';
      if (igParts.length > 0) descriptionClean = descriptionClean.replace(/\n?\[IG Research\].*$/s, '').trim();
      if (seoParts.length > 0) descriptionClean = descriptionClean.replace(/\n?\[SEO\].*$/s, '').trim();

      // Standard lead fields (Business Name, City, Type, …) are surfaced as
      // elegant cards in the UI, so the editable textarea only holds the free
      // description + research context. The standard fields are re-attached for
      // the AI via composeAiContext().
      const reviewContext = formatReviewAuditContext(rowData.researchIntelligence?.googleReviewAudit);
      const connectorContext = formatConnectorContext(rowData.researchIntelligence);
      const contextText = `${descriptionClean}${dmContext}${cfLines ? `\n${cfLines}` : ''}${igContext}${waContext}${seoContext}${reviewContext}${connectorContext}${chanceRadarText ? `\n${chanceRadarText}` : ''}${outreachNotes ? `\n\nIMPORTANT OUTREACH NOTES (consider these carefully):\n${outreachNotes}` : ''}`.trim();
      setContextInfo(contextText);
    }
  }, [rowData, contextFields, selectedEmail, allEmails, chanceRadarText]);

  useEffect(() => {
    const configId = selectedTopicData?.chanceRadarId;
    if (!userId || !rowData?.id || !configId) {
      setChanceRadarText("");
      return undefined;
    }
    let alive = true;
    authenticatedFetch(CLOUD_FUNCTION_URLS.chanceRadar(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "ensure", userId, configId, contactId: rowData.id }),
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!alive) return;
        setChanceRadarText(ok && data.text ? data.text : "");
      })
      .catch(() => {
        if (alive) setChanceRadarText("");
      });
    return () => {
      alive = false;
    };
  }, [userId, rowData?.id, selectedTopicData?.chanceRadarId]);

  // Re-attach the standard lead fields (shown as cards in the UI) to the
  // editable context so the AI always receives complete lead information.
  const composeAiContext = () => {
    if (!rowData) return contextInfo;
    const header = [
      rowData.business_name ? `Business Name: ${rowData.business_name}` : '',
      rowData.lead_city ? `City: ${rowData.lead_city}` : '',
      rowData.lead_type ? `Type: ${rowData.lead_type}` : '',
      rowData.lead_country ? `Country: ${rowData.lead_country}` : '',
    ].filter(Boolean).join('\n');
    return [header, contextInfo].filter(Boolean).join('\n').trim();
  };

  // Nächsten Follow-Up-Schritt ermitteln
  const getNextFollowUpStep = (currentStatus) => {
    switch (currentStatus) {
      case 'Introduced':
        return "Follow Up";
      case 'First Follow-up':
        return "Second Follow Up";
      case 'Second Follow-up':
        return "Third Follow Up";
      case 'Third Follow-up':
        return null; // Alle Follow-ups wurden bereits gesendet
      default:
        return "Introduce Yourself";
    }
  };

  // Topic basierend auf Kontaktstatus setzen
  useEffect(() => {
    if (resendDraft) {
      const topic = topicForPreparedDraft(resendDraft, topicTypeMapping);
      if (topic) {
        setSelectedTopic(topic);
        setIsStatusSet(true);
        return;
      }
    }
    // Prüfe zuerst, ob ein selectedTopic aus den rowData vorhanden ist (von AI Tags)
    if (rowData.selectedTopic) {
      setSelectedTopic(rowData.selectedTopic);
      setIsStatusSet(true);
      return; // Früher Return, um die Standard-Logik zu überspringen
    }
    
    // Robuste Prüfung für lead_contacted - berücksichtige verschiedene mögliche Feldnamen und undefined/null Werte
    const contactedStatus = effectiveContactStatus(rowData) || null;
    const hasReplied = rowData.lead_reply === true || rowData.lead_has_replied === true;
    
    // Wenn der Lead noch nicht kontaktiert wurde (null, undefined, oder "Not Contacted")
    if (!contactedStatus || contactedStatus === "Not Contacted" || contactedStatus === "" || contactedStatus === null || contactedStatus === undefined) {
      setSelectedTopic("Introduce Yourself");
      setIsStatusSet(false);
    } else {
      const nextFollowUp = getNextFollowUpStep(contactedStatus);
      if (nextFollowUp) {
        setSelectedTopic(nextFollowUp);
      } else {
        // Fallback, wenn kein Follow-up bestimmt werden kann
        setSelectedTopic("Introduce Yourself");
      }
      setIsStatusSet(true);
    }
  }, [rowData.lead_contacted, rowData.lead_status, rowData.lead_last_email?.type, rowData.lead_reply, rowData.lead_has_replied, rowData.selectedTopic, resendDraft?.draftId, resendDraft ? topicTypeMapping : null]);

  // Topics für Antworten
  const getAvailableTopics = () => {
    const nextFollowUp = getNextFollowUpStep(effectiveContactStatus(rowData));
    if (!nextFollowUp) {
      return ["Introduce Yourself", "Follow Up", "Second Follow Up", "Third Follow Up"];
    }
    return [nextFollowUp];
  };

  // Hilfsfunktion für Standard-Topic-Anweisungen
  const getDefaultTopicNote = (topicType) => {
    const defaultNotes = {
      'intro': "Bitte stelle unser Unternehmen vor, teile einige spannende Details darüber, wie wir helfen können, und füge einen Call-to-Action hinzu.",
      'follow_up_1': "Erkundige dich höflich, ob der Empfänger die vorherige E-Mail erhalten hat. Halte es kurz und freundlich.",
      'follow_up_2': "Dies ist das zweite Follow-up. Frage höflich nach, ob der Empfänger noch interessiert ist, ohne aufdringlich zu sein.",
      'follow_up_3': "Dies ist das letzte Follow-up. Mache einen letzten sanften Versuch, den Empfänger zu erreichen.",
      'custom_reply': "Bitte verfasse eine durchdachte Antwort auf die Nachricht des Empfängers."
    };
    
    return defaultNotes[topicType] || "Bitte schreibe eine professionelle E-Mail an den Empfänger.";
  };

  // Hilfsfunktion für zufällige Auswahl aus Array (verwendet jetzt selectRandomWithTracking für Tracking)
  const getRandomFromArray = (array) => {
    const result = selectRandomWithTracking(array);
    return result ? result.text : null;
  };

  // Topic-spezifische Notiz holen MIT Tracking-Daten
  // Gibt ein Objekt zurück: { text, instructionId, templateId, templateName }
  const getTopicSpecificNoteWithTracking = () => {
    if (!selectedTopic) return { text: "", instructionId: null, templateId: null, templateName: null };
    
    // 🔥 Hole den Topic-Typ für die Suche (z.B. "Introduce Yourself" → "intro")
    const topicType = topicTypeMapping[selectedTopic];
    console.log('📧 [TRACKING] Looking for topic:', selectedTopic, '→ type:', topicType);
    
    // Finde das Template in userTopics - suche nach topic ODER type
    // Two-pass search: exact name match FIRST, then type-based fallback.
    // This ensures a custom template assigned by name (e.g. "Stepstone") is found
    // before a standard template with the same type (e.g. "Introduce Yourself" with type "intro").
    const findTopicWithId = (searchTopic, searchType) => {
      const searchScopes = [
        ['userTopics', Object.entries(userTopics).filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v) && v.topic)],
        ['selectedTopics', userTopics.selectedTopics ? Object.entries(userTopics.selectedTopics) : []],
        ['customTopics', userTopics.customTopics ? Object.entries(userTopics.customTopics) : []],
      ];

      // Pass 1: exact name match only (topicData.topic === searchTopic)
      for (const [scopeName, entries] of searchScopes) {
        for (const [topicId, topicData] of entries) {
          if (topicData.topic === searchTopic) {
            console.log(`📧 [TRACKING] Found topic by exact name in ${scopeName}:`, topicId, topicData.topic);
            return { topicId, topicData };
          }
        }
      }

      // Pass 2: type-based fallback (topicData.type === searchTopic or searchType)
      for (const [scopeName, entries] of searchScopes) {
        for (const [topicId, topicData] of entries) {
          if (topicData.type === searchTopic || (searchType && topicData.type === searchType)) {
            console.log(`📧 [TRACKING] Found topic by type in ${scopeName}:`, topicId, topicData.topic);
            return { topicId, topicData };
          }
        }
      }

      console.log('📧 [TRACKING] No topic found for:', searchTopic, searchType);
      return null;
    };
    
    // Suche nach dem Topic (mit dem Topic-Typ als Fallback)
    const found = findTopicWithId(selectedTopic, topicType);
    
    if (found) {
      const { topicId, topicData } = found;
      
      // Extract General Info fields from topic data
      const templateMeta = {
        generalInfo: topicData.generalInfo || '',
        aiAdherence: topicData.aiAdherence || 'exact',
        formattingStyle: topicData.formattingStyle || 'as-written',
        model: topicData.model || null,
        maxTokens: topicData.maxTokens || null,
        writingLevel: topicData.writingLevel || 'middle_school',
        aiTemperature: topicData.aiTemperature != null ? topicData.aiTemperature : null,
        hyperIntelligence: topicData.hyperIntelligence || false,
        personalizationGuide: topicData.personalizationGuide || '',
        referenceFile: topicData.referenceFile || null,
        ultraMode: topicData.ultraMode || false,
        smartGreeting: topicData.smartGreeting || false,
        greetingCasualPrefixes: Array.isArray(topicData.greetingCasualPrefixes) ? topicData.greetingCasualPrefixes : (Array.isArray(topicData.greetingPrefixes) ? topicData.greetingPrefixes : ['Hey', 'Hi', 'Hello']),
        greetingFormalPrefixes: Array.isArray(topicData.greetingFormalPrefixes) ? topicData.greetingFormalPrefixes : ['Dear'],
        smartSubjectLine: topicData.smartSubjectLine || false,
        smartSubjectStyle: topicData.smartSubjectStyle || 'neutral',
        smartSubjectInstructions: topicData.smartSubjectInstructions || '',
        staticSubjectLines: Array.isArray(topicData.subjectLines) ? topicData.subjectLines : [],
        language: topicData.language || null,
        emailPreference: topicData.emailPreference || 'auto',
        subjectMode: topicData.subjectMode === 'reply_to_previous' ? 'reply_to_previous' : 'new',
        replyPrefix: topicData.replyPrefix || 'auto',
        replyPrefixCustom: topicData.replyPrefixCustom || '',
        // Voice-Clone-mode passthrough (additive — standard-mode code never reads these).
        // Routing happens in handleGenerate via isVoiceCloneTopicData(templateData).
        mode: topicData.mode || 'standard',
        cloneConfig: topicData.cloneConfig || null,
      };
      
      // Prüfe auf mehrere Instructions (Array) – pacing-aware selection
      if (topicData.topicSpecificNotes && Array.isArray(topicData.topicSpecificNotes) && topicData.topicSpecificNotes.length > 0) {
        // Variant selection uses a rotation / shuffle-bag (keyed per template)
        // so all variants get used once before any repeats, instead of pure
        // random which clusters on a few. The contact's lead_pacing is no
        // longer used; the greeting tone is driven solely by the SELECTED
        // variant's own pacing classification (see greetingTone below).
        // lastInstructionIdRef is still passed as an extra exclude so a manual
        // regenerate never returns the exact same variant twice in a row.
        const selected = selectInstructionWithRotation(topicData.topicSpecificNotes, null, topicId, lastInstructionIdRef.current);
        console.log('🎲 [ROTATION-TOPIC] Selected instruction:', selected, '(rotationKey:', topicId, ', extraExclude:', lastInstructionIdRef.current, ')');
        return {
          text: selected ? (selected.text || '') : "",
          instructionId: selected ? (selected.id || null) : null,
          selectedPacing: selected ? (selected.pacing || null) : null,
          templateId: topicId,
          templateName: topicData.topic || topicData.type,
          ...templateMeta
        };
      }
      // Backwards compatibility: Einzelne Instruction
      else if (topicData.topicSpecificNote) {
        console.log('🎲 [RANDOM-TOPIC] Single instruction found:', topicData.topicSpecificNote);
        return {
          text: topicData.topicSpecificNote,
          instructionId: `legacy_${topicId}`,
          templateId: topicId,
          templateName: topicData.topic || topicData.type,
          ...templateMeta
        };
      } else {
        return {
          text: "",
          instructionId: null,
          templateId: topicId,
          templateName: topicData.topic || topicData.type,
          ...templateMeta
        };
      }
    }
    
    // Kein Template gefunden - Fallback auf Standard-Anweisungen
    console.log('📧 [TRACKING] No template found, using default note for type:', topicType);
    return {
      text: topicType ? getDefaultTopicNote(topicType) : "",
      instructionId: null,
      templateId: null,
      templateName: null,
      generalInfo: '',
      aiAdherence: 'exact',
      formattingStyle: 'as-written',
      model: null,
      maxTokens: null,
      writingLevel: 'middle_school',
      aiTemperature: null,
      hyperIntelligence: false,
      personalizationGuide: '',
      referenceFile: null,
      ultraMode: false,
      smartGreeting: false,
      greetingCasualPrefixes: ['Hey', 'Hi', 'Hello'],
      greetingFormalPrefixes: ['Dear'],
      smartSubjectLine: false,
      smartSubjectStyle: 'neutral',
      smartSubjectInstructions: '',
      staticSubjectLines: [],
      language: null,
      subjectMode: 'new',
      replyPrefix: 'auto',
      replyPrefixCustom: '',
    };
  };
  
  // Wrapper für Backwards-Kompatibilität
  const getTopicSpecificNote = () => {
    return getTopicSpecificNoteWithTracking().text;
  };

  // Shared CopyTwin Re-Generate state: is the active template a CopyTwin, what
  // is its default clone, and which trained clones can we switch to. Used by
  // both the 3-dots framework picker and the standalone quick popover.
  const cloneRegenState = useMemo(() => {
    const meta = getTopicSpecificNoteWithTracking();
    const isCloneRegen = isVoiceCloneTopicData(meta);
    const defaultCloneId = meta?.cloneConfig?.cloneId || null;
    const switchableClones = isCloneRegen
      ? [...(copyClones || [])]
          .filter(c => c && c.id && (c.voiceDNA || c.status === 'ready' || c.id === defaultCloneId))
          .sort((a, b) => (a.id === defaultCloneId ? -1 : b.id === defaultCloneId ? 1 : 0))
      : [];
    return { isCloneRegen, defaultCloneId, switchableClones };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopic, userTopics, copyClones]);

  const cloneRegenLabel = (c) => c?.name?.trim() || autoCloneName(c) || 'CopyTwin';

  // Betreffzeile für das Topic holen MIT Tracking
  const getSubjectLineForTopicWithTracking = async (topic) => {
    if (!topic || !rowData) return { text: "", subjectLineId: null };
    
    // Hole den topicType für die Suche (z.B. "Introduce Yourself" → "intro")
    const topicType = topicTypeMapping[topic];
    console.log('📧 [SUBJECT-TRACKING] Looking for subject line, topic:', topic, '→ type:', topicType);
    
    // Two-pass search: exact name match FIRST, then type-based fallback.
    const findMatchingTopic = (searchTopic, searchType) => {
      const searchScopes = [
        ['userTopics', Object.entries(userTopics).filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v) && v.topic)],
        ['selectedTopics', userTopics.selectedTopics ? Object.entries(userTopics.selectedTopics) : []],
        ['customTopics', userTopics.customTopics ? Object.entries(userTopics.customTopics) : []],
      ];

      // Pass 1: exact name match
      for (const [scopeName, entries] of searchScopes) {
        for (const [topicId, topicData] of entries) {
          if (topicData.topic === searchTopic) {
            console.log(`📧 [SUBJECT-TRACKING] Found topic by exact name in ${scopeName}:`, topicId, topicData.topic);
            return { topicId, topicData };
          }
        }
      }

      // Pass 2: type-based fallback
      for (const [scopeName, entries] of searchScopes) {
        for (const [topicId, topicData] of entries) {
          if (topicData.type === searchTopic || (searchType && topicData.type === searchType)) {
            console.log(`📧 [SUBJECT-TRACKING] Found topic by type in ${scopeName}:`, topicId, topicData.topic);
            return { topicId, topicData };
          }
        }
      }

      return null;
    };
    
    // Suche mit topic UND topicType
    const found = findMatchingTopic(topic, topicType);
    
    if (found) {
      const { topicId, topicData } = found;

      // Follow-up reply-to-previous: derive subject from contact's last email
      if (topicData.subjectMode === 'reply_to_previous') {
        const replySubject = resolveFollowUpSubject(topicData, rowData);
        if (replySubject) {
          console.log('📧 [SUBJECT-TRACKING] Using reply-to-previous subject:', replySubject);
          return { text: replacePlaceholders(replySubject), subjectLineId: SUBJECT_REPLY_LINE_ID };
        }
        console.warn('📧 [SUBJECT-TRACKING] reply_to_previous requested but contact has no lead_last_email.subject — falling back to variants');
      }

      // Prüfe auf mehrere Subject Lines (Array)
      if (topicData.subjectLines && Array.isArray(topicData.subjectLines) && topicData.subjectLines.length > 0) {
        console.log('🎲 [ROTATION-TOPIC] Multiple subject lines found, selecting via rotation:', topicData.subjectLines);
        // Rotation / shuffle-bag (keyed per template) so all subject lines are
        // used once before any repeats, instead of pure random clustering.
        const selected = selectRandomWithRotation(topicData.subjectLines, topicId);
        console.log('🎲 [ROTATION-TOPIC] Selected subject line:', selected, '(rotationKey:', topicId, ')');
        const text = replacePlaceholders(selected ? selected.text : "");
        return { text, subjectLineId: selected ? selected.id : null };
      }
      // Backwards compatibility: Einzelne Subject Line
      else if (topicData.subjectLine) {
        console.log('🎲 [RANDOM-TOPIC] Single subject line found:', topicData.subjectLine);
        const text = replacePlaceholders(topicData.subjectLine);
        return { text, subjectLineId: `legacy_${topicId}` };
      } else {
        return { text: "", subjectLineId: null };
      }
    }
    
    console.log('📧 [SUBJECT-TRACKING] No matching topic found, using fallbacks');
    
    if (userSettings.subject_line) {
      return { text: replacePlaceholders(userSettings.subject_line), subjectLineId: 'user_default' };
    }
    
    const defaultSubjects = {
      "Introduce Yourself": `Boost for ${rowData.business_name || 'Ihr Unternehmen'}`,
      "Follow Up": `Nachfrage - Partnerschaft mit ${rowData.business_name || 'Ihrem Unternehmen'}`,
      "Second Follow Up": `Kurzes Update für ${rowData.business_name || 'Ihr Unternehmen'}`,
      "Third Follow Up": `Nachfrage für ${rowData.business_name || 'Ihr Unternehmen'}`
    };
    
    return { text: defaultSubjects[topic] || "", subjectLineId: 'default' };
  };
  
  // Wrapper für Backwards-Kompatibilität
  const getSubjectLineForTopic = async (topic) => {
    const result = await getSubjectLineForTopicWithTracking(topic);
    return result.text;
  };
  
  // Für useEffect - Betreffzeile mit Tracking
  const updateSubjectLineWithTracking = async () => {
    const result = await getSubjectLineForTopicWithTracking(selectedTopic);
    setSubjectLine(result.text);
    setTrackingData(prev => ({
      ...prev,
      subjectLineId: result.subjectLineId
    }));
  };

  const handleRegenerateSubjectLines = async () => {
    if (subjectAltsLoading) return;
    // Precomputed alternatives sit in state right after "Generate" finishes —
    // first click opens them instantly (no API call). Second click clears the
    // precompute flag so we fetch fresh brand-voice alternatives.
    if (subjectAltsPrecomputed && subjectAlts.length > 0 && !subjectAltsOpen) {
      setSubjectAltsOpen(true);
      setSubjectAltsPrecomputed(false);
      return;
    }
    if (subjectAltsOpen && subjectAlts.length > 0) {
      setSubjectAltsOpen(false);
      return;
    }
    setSubjectAltsOpen(true);
    setSubjectAltsLoading(true);
    setSubjectAlts([]);
    setSubjectAltsPrecomputed(false);

    try {
      const templateData = getTopicSpecificNoteWithTracking();
      const recipient = getRecipientInfoForContact(rowData, selectedEmail);
      const language = templateData.language || aiSettings.language || 'auto';
      const mergedInstructions = [aiSettings.subjectLineGuidelines, templateData.smartSubjectInstructions].filter(Boolean).join('\n') || '';

      // Subject variants the user authored WITH {instruction} prompts. If present,
      // the backend resolves those prompts (against the finished body) instead of
      // the generic suggestion prompt — so the button honors what the user crafted.
      const subjectInstrTemplates = (templateData.staticSubjectLines || [])
        .map(sl => (sl && typeof sl === 'object' ? sl.text : sl))
        .filter(t => typeof t === 'string' && t.trim() && hasInstructionBlocks(t))
        .map(t => replacePlaceholders(t));

      // Static subject variants (no {instruction}) — passed as BRAND-VOICE
      // reference so the suite matches campaign tone instead of generic copy.
      const staticVoiceLines = (templateData.staticSubjectLines || [])
        .map(sl => (sl && typeof sl === 'object' ? sl.text : sl))
        .filter(t => typeof t === 'string' && t.trim() && !hasInstructionBlocks(t))
        .map(t => replacePlaceholders(t));

      // CopyTwin-aware suggestions: when the active template is a Voice-Clone
      // template, pass the configured clone + campaign brief so the backend
      // generates the 4 alternatives in the writer's trained subject voice
      // (DNA, tactics, ✓/✗ calibration) instead of the generic prompt.
      const cloneSubjectContext = cloneRegenState.isCloneRegen && cloneRegenState.defaultCloneId
        ? {
            cloneId: cloneRegenState.defaultCloneId,
            cloneConfig: templateData.cloneConfig || null,
            userId,
            senderName: userSettings?.user_name || '',
            recipientCity: rowData.lead_city || '',
            recipientCountry: rowData.lead_country || '',
            recipientWebsite: rowData.lead_website || '',
            businessDescription: rowData.lead_description || '',
          }
        : {};

      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.generateSubjectLines(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: rowData.business_name || '',
          firstName: getFirstDmName(rowData),
          context: composeAiContext() || `Business: ${rowData.business_name || ''}. Website: ${rowData.lead_website || ''}.`,
          language,
          recipientName: recipient.name,
          recipientRole: recipient.role,
          style: templateData.smartSubjectStyle || 'neutral',
          userInstructions: mergedInstructions,
          existingSubjects: [],
          customFieldsData: (contextFields || []).reduce((acc, cf) => {
            const val = rowData[cf.key];
            if (val != null && val !== '') acc.push({ key: cf.key, label: cf.label, value: String(val) });
            return acc;
          }, []),
          currentSubject: subjectLine,
          emailBody: bodyContent || '',
          writingLevel: templateData.writingLevel || 'middle_school',
          senderInfo: templateData.generalInfo || '',
          topicName: selectedTopic || '',
          topicSpecificNote: templateData.text || '',
          subjectLineTemplates: subjectInstrTemplates,
          staticSubjectLinesForVoice: staticVoiceLines,
          ...cloneSubjectContext
        })
      });

      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setSubjectAlts(data.subjectLines || []);
    } catch (err) {
      console.error('Subject line generation failed:', err);
      setSubjectAlts([]);
    } finally {
      setSubjectAltsLoading(false);
    }
  };

  const handleSelectSubjectAlt = (alt) => {
    setSubjectLine(alt);
    setTrackingData(prev => ({ ...prev, subjectLineId: 'smart_subject' }));
    setSubjectAltsOpen(false);
    setSubjectAltsPrecomputed(false);
  };

  const replacePlaceholders = (text) => {
    if (!text) return '';
    const dmFirstName = getFirstDmName(rowData);
    let dmFullName = '';
    let dmLastName = rowData.lead_lastname || '';
    // Use the decision maker we already contacted (or first DM) so the name
    // placeholders match the recipient chosen by resolvePreferredEmail.
    const primaryDm = getPrimaryDecisionMaker(rowData);
    if (primaryDm && primaryDm.name) {
      dmFullName = primaryDm.name;
      const parts = primaryDm.name.split(' ');
      if (parts.length > 1) dmLastName = parts.slice(1).join(' ');
    }

    const recipient = getRecipientInfoForContact(rowData, selectedEmail);

    // SEO Analyzer variables (empty strings if no scan exists yet)
    const seo = rowData?.researchIntelligence?.seoAnalysis;
    const seoVisibility = seo?.data?.core?.visibility?.value != null
      ? Number(seo.data.core.visibility.value).toFixed(2) : '';
    const seoTopKeyword = Array.isArray(seo?.data?.opportunities) && seo.data.opportunities[0]
      ? seo.data.opportunities[0].keyword : '';
    const seoMainCompetitor = Array.isArray(seo?.data?.competitors) && seo.data.competitors[0]
      ? seo.data.competitors[0].domain : '';
    const seoBacklinks = seo?.data?.backlinks?.total != null
      ? String(seo.data.backlinks.total) : '';
    const seoKeywordsTotal = seo?.data?.core?.keywordsTotal != null
      ? String(seo.data.core.keywordsTotal) : '';
    const seoKeywordsTop10 = seo?.data?.core?.keywordsTop10 != null
      ? String(seo.data.core.keywordsTop10) : '';
    const seoTraffic = seo?.data?.trafficValue?.estimatedTrafficMonthly != null
      ? Number(seo.data.trafficValue.estimatedTrafficMonthly).toLocaleString('de-DE') : '';
    const seoTrafficValue = seo?.data?.trafficValue?.estimatedValueMonthly
      ? `${Number(seo.data.trafficValue.estimatedValueMonthly).toLocaleString('de-DE')} €` : '';
    const seoTrend = seo?.data?.visibilityTrend?.changePct != null
      ? `${seo.data.visibilityTrend.changePct > 0 ? '+' : ''}${seo.data.visibilityTrend.changePct}% (${seo.data.visibilityTrend.overWindow || '12M'})` : '';

    return text
      .replace(/{{\s*business_name\s*}}/g, rowData.business_name || '')
      .replace(/{{\s*lead_name\s*}}/g, dmFullName || rowData.lead_name || '')
      .replace(/{{\s*lead_firstname\s*}}/g, dmFirstName)
      .replace(/{{\s*lead_lastname\s*}}/g, dmLastName)
      .replace(/{{\s*recipient_name\s*}}/g, recipient.name)
      .replace(/{{\s*recipient_role\s*}}/g, recipient.role)
      .replace(/{{\s*user_name\s*}}/g, userSettings.user_name || '')
      .replace(/{{\s*company_name\s*}}/g, userSettings.company_name || 'BonusClub')
      .replace(/{{\s*footer_text\s*}}/g, () => accountTextSignature(senderEmails.find(a => a.email === selectedSenderEmail)))
      .replace(/{{\s*seo_visibility\s*}}/g, seoVisibility)
      .replace(/{{\s*seo_top_keyword\s*}}/g, seoTopKeyword)
      .replace(/{{\s*seo_main_competitor\s*}}/g, seoMainCompetitor)
      .replace(/{{\s*seo_backlinks\s*}}/g, seoBacklinks)
      .replace(/{{\s*seo_keywords_total\s*}}/g, seoKeywordsTotal)
      .replace(/{{\s*seo_keywords_top10\s*}}/g, seoKeywordsTop10)
      .replace(/{{\s*seo_traffic\s*}}/g, seoTraffic)
      .replace(/{{\s*seo_traffic_value\s*}}/g, seoTrafficValue)
      .replace(/{{\s*seo_trend\s*}}/g, seoTrend);
  };

  // Betreffzeile aktualisieren, wenn sich Topic ändert (MIT Tracking)
  useEffect(() => {
    if (!selectedTopic || !rowData || Object.keys(userTopics).length === 0) return;
    if (resendDraft) return;
    
    const updateSubjectLineAsync = async () => {
      try {
        const result = await getSubjectLineForTopicWithTracking(selectedTopic);
        if (hasInstructionBlocks(result.text)) {
          subjectTemplateRef.current = result.text;
          setSubjectLine('');
        } else {
          subjectTemplateRef.current = null;
          setSubjectLine(result.text);
        }
        setTrackingData(prev => ({
          ...prev,
          subjectLineId: result.subjectLineId
        }));
      } catch (error) {
        subjectTemplateRef.current = null;
        setSubjectLine(`${rowData.business_name || 'Ihr Unternehmen'}`);
      }
    };
    
    const timeoutId = setTimeout(() => {
      updateSubjectLineAsync();
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [selectedTopic, userTopics, rowData]);

  // Automatische Generierung beim ersten Laden
  useEffect(() => {
    if (
      !hasAutoGenerated.current && 
      isTopicsLoaded && 
      selectedTopic
    ) {
      hasAutoGenerated.current = true;
      handleGenerate(undefined, rowData?.taskBrief || undefined);
    }
  }, [isTopicsLoaded, selectedTopic]);

  // Generate all dynamic AI images referenced by {{image:token}} in the given
  // body text, for the current contact. Runs after the email text is created.
  const triggerAiImages = async (bodyText, { force = false } = {}) => {
    const tokens = collectAiImageTokens(bodyText, userSettings?.imageLabAssets);
    if (tokens.length === 0) {
      setAiImages({});
      return;
    }
    // Fresh run reseeds from the current body, so clear any prior cancellations.
    removedImageTokensRef.current.clear();
    // Seed loading state for every referenced AI token.
    setAiImages((prev) => {
      const next = {};
      tokens.forEach(({ token, asset }) => {
        const existing = prev[token];
        next[token] = existing && existing.status === "ready" && !force
          ? existing
          : { status: "loading", name: asset.name || token };
      });
      return next;
    });

    await Promise.all(
      tokens.map(async ({ token, asset }) => {
        const existing = aiImages[token];
        if (existing && existing.status === "ready" && !force) return;
        try {
          const gen = await generateAiImageForContact({
            userId,
            token,
            contactId: rowData?.id,
            contact: rowData,
            userSettings,
            selectedEmail,
            force,
          });
          if (removedImageTokensRef.current.has(token)) return; // cancelled meanwhile
          setAiImages((prev) => ({
            ...prev,
            [token]: {
              status: "ready",
              imageUrl: gen.imageUrl,
              altText: gen.altText,
              maxWidth: gen.maxWidth,
              align: gen.align,
              linkUrl: gen.linkUrl,
              width: gen.width,
              height: gen.height,
              name: asset.name || token,
            },
          }));
        } catch (err) {
          console.error(`[NormalMode] AI image "${token}" failed:`, err);
          if (removedImageTokensRef.current.has(token)) return; // cancelled meanwhile
          setAiImages((prev) => ({
            ...prev,
            [token]: { status: "error", error: err.message || "Generation failed", name: asset.name || token },
          }));
        }
      })
    );
  };

  const regenerateAiImage = async (token) => {
    const asset = userSettings?.imageLabAssets?.[token];
    removedImageTokensRef.current.delete(token); // explicit regenerate re-enables it
    setAiImages((prev) => ({ ...prev, [token]: { status: "loading", name: asset?.name || token } }));
    try {
      const gen = await generateAiImageForContact({
        userId,
        token,
        contactId: rowData?.id,
        contact: rowData,
        userSettings,
        selectedEmail,
        force: true,
      });
      if (removedImageTokensRef.current.has(token)) return; // cancelled meanwhile
      setAiImages((prev) => ({
        ...prev,
        [token]: {
          status: "ready",
          imageUrl: gen.imageUrl,
          altText: gen.altText,
          maxWidth: gen.maxWidth,
          align: gen.align,
          linkUrl: gen.linkUrl,
          width: gen.width,
          height: gen.height,
          name: asset?.name || token,
        },
      }));
    } catch (err) {
      if (removedImageTokensRef.current.has(token)) return; // cancelled meanwhile
      setAiImages((prev) => ({
        ...prev,
        [token]: { status: "error", error: err.message || "Generation failed", name: asset?.name || token },
      }));
    }
  };

  // Refine an image (ChatGPT-style): feed the CURRENTLY generated image back to
  // the model as reference and apply the user's change request, replacing the
  // old image inline. Falls back to an error chip if it fails.
  const refineAiImage = async (token, refinePrompt) => {
    const asset = userSettings?.imageLabAssets?.[token];
    const current = aiImages[token];
    const refineImageUrl = current?.imageUrl;
    if (!refineImageUrl || !refinePrompt || !String(refinePrompt).trim()) return;
    removedImageTokensRef.current.delete(token);
    setAiImages((prev) => ({
      ...prev,
      [token]: { ...(prev[token] || {}), status: "loading", name: asset?.name || token },
    }));
    try {
      const gen = await generateAiImageForContact({
        userId,
        token,
        contactId: rowData?.id,
        contact: rowData,
        userSettings,
        selectedEmail,
        force: true,
        refinePrompt,
        refineImageUrl,
      });
      if (removedImageTokensRef.current.has(token)) return; // cancelled meanwhile
      setAiImages((prev) => ({
        ...prev,
        [token]: {
          status: "ready",
          imageUrl: gen.imageUrl,
          altText: gen.altText,
          maxWidth: gen.maxWidth,
          align: gen.align,
          linkUrl: gen.linkUrl,
          width: gen.width,
          height: gen.height,
          name: asset?.name || token,
        },
      }));
    } catch (err) {
      if (removedImageTokensRef.current.has(token)) return; // cancelled meanwhile
      setAiImages((prev) => ({
        ...prev,
        [token]: { status: "error", error: err.message || "Refinement failed", name: asset?.name || token },
      }));
    }
  };

  // Remove an image: drop its state AND strip the {{image:token}} placeholder
  // from the body so it is neither regenerated nor sent.
  const removeAiImage = (token) => {
    // Mark cancelled so any in-flight generation for this token is discarded.
    removedImageTokensRef.current.add(token);
    setAiImages((prev) => {
      const next = { ...prev };
      delete next[token];
      return next;
    });
    const stripRe = new RegExp(`\\n?\\{\\{\\s*image:${token}\\s*\\}\\}\\n?`, "g");
    setBodyContent((prev) => prev.replace(stripRe, "\n"));
  };

  // E-Mail generieren
  const handleGenerate = async (frameworkPromptOverride, customInstructions, cloneIdOverride) => {
    // Cheapest possible email as the gate: the exact price only becomes known
    // once the template is resolved further down, and this only has to catch the
    // "nothing is possible at all" case. Anything in between is still caught by
    // the per-action charge, which surfaces the same dialog.
    if (!requireCredits(emailGenerationCost({}))) {
      setIsGenerating(false);
      return;
    }

    const topic = selectedTopic;
    const prompt = composeAiContext();

    if (randomSenderMode && senderEmails.length > 1) {
      const others = senderEmails.filter(e => e.email !== selectedSenderEmail);
      const pick = others[Math.floor(Math.random() * others.length)];
      setSelectedSenderEmail(pick.email);
    }

    setIsGenerating(true);
    setBodyContent("");
    setSendSuccess(false);
    // Stale precomputed alts from a previous generation would open a wrong
    // dropdown on the ⚡ button — clear the flag now; a fresh set will be
    // seeded when the response comes back (if the suite produced any).
    setSubjectAltsPrecomputed(false);

    try {
      // 🔥 WICHTIG: Verwende die neue getTopicSpecificNoteWithTracking() Funktion mit Tracking!
      const templateData = getTopicSpecificNoteWithTracking();
      const { text: topicSpecificNote, instructionId, selectedPacing, templateId, templateName, generalInfo, aiAdherence, formattingStyle, model: templateModel, maxTokens: templateMaxTokens, writingLevel: templateWritingLevel, aiTemperature: templateAiTemperature, hyperIntelligence, personalizationGuide, referenceFile: topicReferenceFile, ultraMode: topicUltraMode, smartGreeting, greetingCasualPrefixes, greetingFormalPrefixes, smartSubjectLine, smartSubjectStyle, smartSubjectInstructions, staticSubjectLines, language: templateLanguage, emailPreference: templateEmailPref, subjectMode: templateSubjectMode } = templateData;
      const isReplyToPrevious = templateSubjectMode === 'reply_to_previous';
      console.log('📧 [NORMAL-GENERATE] Using topic-specific note with tracking:', { topicSpecificNote, instructionId, templateId, templateName, aiAdherence, formattingStyle, templateModel, templateMaxTokens, hyperIntelligence, templateLanguage, templateAiTemperature });
      
      lastInstructionIdRef.current = instructionId;
      setTrackingData(prev => ({
        ...prev,
        templateId,
        templateName,
        instructionId,
        // Reset variantId every time we (re)generate. It's only set again
        // by the Voice-Clone branch below when a Voice-Clone send happens.
        variantId: null,
      }));

      // ===== VOICE-CLONE BRANCH =====
      // Voice-Clone-mode templates skip the entire standard-mode pipeline
      // (topic notes, smart greeting, smart subject etc.) and route through
      // the Voice-Clone backend, which loads the clone + template from RTDB
      // and generates with the writer's voice DNA.
      if (isVoiceCloneTopicData(templateData)) {
        // Re-read the contact from Firestore so freshly-run research (Spy / IG /
        // SEO / Website) reaches the generator even if the table snapshot the
        // popup opened with is stale. Fail-safe: returns the snapshot on error.
        const freshRow = await loadFreshContactRow(userId, rowData);
        const recipient = getRecipientInfoForContact(freshRow, selectedEmail);
        const customFieldsData = (contextFields || []).reduce((acc, cf) => {
          const val = freshRow[cf.key];
          if (val != null && val !== '') acc.push({ key: cf.key, label: cf.label, value: String(val) });
          return acc;
        }, []);

        // CopyTwin switch: a Re-Generate may pick an alternative trained clone
        // for this single send. We never mutate the template — the override is
        // only threaded into this one payload + credit ledger entry.
        const effectiveCloneId = cloneIdOverride || templateData.cloneConfig?.cloneId;

        const vcPayload = buildVoiceClonePayload({
          topicData: templateData,
          templateId,
          rowData: freshRow,
          userSettings,
          // Active workspace userId so the backend resolves the right RTDB
          // path when the caller sits inside a switched team workspace.
          userId,
          recipient,
          firstName: getFirstDmName(freshRow),
          customFieldsData,
          cloneIdOverride: cloneIdOverride || undefined,
          // SmartSubject toggle is the single source of truth — skip the
          // backend subject call when the user picked manual subjects in the
          // editor, AND always skip for reply-to-previous threads (which
          // inherit the original subject).
          includeSubject: !isReplyToPrevious && shouldGenerateSmartSubject(templateData),
          textSignature: accountTextSignature(senderEmails.find(a => a.email === selectedSenderEmail)),
        });

        if (!vcPayload) {
          // Defensive: a voice-clone template with no clone selected. Surface
          // a clear UX error rather than silently sending through the
          // standard pipeline (which would produce a generic email).
          alert('This CopyTwin template has no twin selected. Open the template editor and pick one before generating.');
          return;
        }

        // Credit deduction — priced centrally (config/creditPlans.js) so every
        // send path charges the same. Legacy 4-tier ids resolve inside.
        const vcTierId = templateData.cloneConfig?.qualityTier || 'best';
        const vcCreditCost = emailGenerationCost({ voiceCloneTier: vcTierId });
        try {
          await chargeCredits({
            action: 'voice_clone_send',
            amount: vcCreditCost,
            worldId: userId,
            metadata: {
              contactId: rowData?.id,
              templateId,
              cloneId: effectiveCloneId,
              qualityTier: vcTierId,
            },
          });
        } catch (creditErr) {
          if (creditErr.code === 'insufficient_credits') {
            // The dialog explains what is paused and how to top up — far more
            // useful than a bare alert.
            openCreditsModal(vcCreditCost);
            setIsGenerating(false);
            return;
          }
          // Transient errors were already retried inside chargeCredits. Going
          // ahead now would generate a 12.5–22 credit mail for free.
          alert('Die Credits für diese CopyTwin E-Mail konnten nicht gebucht werden. Es wurde nichts generiert — bitte erneut versuchen.');
          return;
        }

        const vcResponse = await authenticatedFetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(vcPayload),
        });
        if (!vcResponse.ok) {
          // Parse the structured backend error so a fixable misconfiguration
          // (e.g. NO_ACTIVE_VARIANT) surfaces a clear, actionable message to
          // the user instead of a cryptic "500".
          const parsed = await readVoiceCloneError(vcResponse);
          throw buildVoiceCloneError(parsed);
        }
        const vcData = await vcResponse.json();
        setBodyContent(vcData.emailContent || '');
        triggerAiImages(vcData.emailContent || '');

        // Subject — 3-stage fallback (parity with bulk path):
        //   1. Backend smart subject (vcData.subjectLine)
        //   2. Random static subject from the template (if configured)
        //   3. Topic / template name with first-name placeholder filled in
        // The topic-name fallback is critical when (a) the backend
        // subject generator returned null (e.g. Claude+OpenAI both down)
        // AND (b) the template has no static subjects configured. Without
        // it the subject field stays empty and the user has to type one
        // manually — the symptom the user reported.
        if (!isReplyToPrevious) {
          if (vcData.subjectLine) {
            setSubjectLine(vcData.subjectLine);
            setTrackingData(prev => ({
              ...prev,
              // Variant-scoped stats bucket so each A/B structure tracks
              // its own subject performance — falls back to the legacy
              // un-suffixed id when variantId is still unknown.
              subjectLineId: buildVoiceCloneSmartSubjectId(vcData.variantId),
            }));
          } else if (Array.isArray(staticSubjectLines) && staticSubjectLines.length > 0) {
            const pick = staticSubjectLines[Math.floor(Math.random() * staticSubjectLines.length)];
            const pickText = typeof pick === 'object' ? pick.text : pick;
            const pickId   = typeof pick === 'object' ? pick.id   : null;
            setSubjectLine(replacePlaceholders(pickText || ''));
            setTrackingData(prev => ({ ...prev, subjectLineId: pickId }));
          } else {
            // Last-resort fallback: template / topic name or
            // "Hi {firstName}". Mirrors what the bulk helper uses
            // (callVoiceCloneApi in bulkModeHelpers.js). The user can
            // always edit before sending — but shipping an email with
            // NO subject is worse than shipping one with a generic-but-
            // functional subject.
            const fnPart = getFirstDmName(rowData) || '';
            const fallback = templateName
              || templateData?.topic
              || (fnPart ? `Hi ${fnPart}`.trim() : 'Quick question');
            console.warn(
              '[VOICE-CLONE] Smart subject returned null AND no static ' +
              'subjects configured — using last-resort fallback:',
              fallback,
            );
            setSubjectLine(replacePlaceholders(fallback));
            setTrackingData(prev => ({ ...prev, subjectLineId: null }));
          }
        }

        // Variant tracking — backend tells us which structure won the
        // weighted-random draw; we pin it on trackingData so the send call
        // can credit stats.variantStats[variantId].
        if (vcData.variantId) {
          setTrackingData(prev => ({ ...prev, variantId: vcData.variantId }));
        }

        console.log('🎯 [VOICE-CLONE] Generated', {
          variantId: vcData.variantId,
          variantLabel: vcData.variantLabel,
          structureId: vcData.structureId,
          qualityTier: vcData.qualityTier,
          modelUsed: vcData.modelUsed,
        });
        return;
      }
      // ===== END VOICE-CLONE BRANCH — standard-mode flow below is unchanged =====
      
      // Per-template model/language config with global fallback
      const model = templateModel || aiSettings.model || 'gpt-4.1-mini';
      const maxTokens = templateMaxTokens || parseInt(aiSettings.max_tokens || aiSettings.maxTokens) || 500;
      const language = templateLanguage || aiSettings.language || 'auto';

      const recipient = getRecipientInfoForContact(rowData, selectedEmail);
      const isDm = allEmails.some(e => e.email === selectedEmail && e.source === 'decision_maker') || !!(recipient?.name);
      const effectivePacing = getEffectivePacing(rowData?.lead_pacing || null, isDm, templateEmailPref);
      const customFieldsData = (contextFields || []).reduce((acc, cf) => {
        const val = rowData[cf.key];
        if (val != null && val !== '') {
          acc.push({ key: cf.key, label: cf.label, value: String(val) });
        }
        return acc;
      }, []);

      const requestData = {
        topic,
        context: prompt,
        businessName: rowData.business_name || '',
        website: rowData.lead_website || '',
        senderName: userSettings.user_name || '',
        senderEmail: userSettings.user_email || '',
        textSignature: accountTextSignature(senderEmails.find(a => a.email === selectedSenderEmail)),
        firstName: getFirstDmName(rowData),
        recipientName: recipient.name,
        recipientRole: recipient.role,
        topicSpecificNote: topicSpecificNote,
        model,
        maxTokens,
        language,
        generalInfo: generalInfo || '',
        aiAdherence: aiAdherence || 'exact',
        formattingStyle: formattingStyle || 'as-written',
        writingLevel: templateWritingLevel || 'middle_school',
        ...(templateAiTemperature != null ? { aiTemperature: templateAiTemperature } : {}),
        hyperIntelligence: hyperIntelligence || false,
        personalizationGuide: personalizationGuide || '',
        referenceFile: topicReferenceFile || null,
        ultraMode: topicUltraMode || false,
        smartGreeting: smartGreeting || false,
        greetingCasualPrefixes: greetingCasualPrefixes || ['Hey', 'Hi', 'Hello'],
        greetingFormalPrefixes: greetingFormalPrefixes || ['Dear'],
        greetingTone: greetingToneFromVariant(selectedPacing),
        smartSubjectLine: isReplyToPrevious ? false : (smartSubjectLine || false),
        smartSubjectStyle: smartSubjectStyle || 'neutral',
        smartSubjectInstructions: [aiSettings.subjectLineGuidelines, smartSubjectInstructions].filter(Boolean).join('\n') || '',
        existingSubjects: [],
        leadPacing: effectivePacing,
        userId,
        templateId,
        contactId: rowData.id || null,
        customFieldsData,
        ...(frameworkPromptOverride ? { frameworkPrompt: frameworkPromptOverride } : {}),
        ...(customInstructions ? { customInstructions } : {}),
        ...(() => {
          let tmpl = subjectTemplateRef.current;
          if (!tmpl && !subjectLine && staticSubjectLines && staticSubjectLines.length > 0) {
            const match = staticSubjectLines.find(sl => hasInstructionBlocks(typeof sl === 'object' ? sl.text : sl));
            if (match) {
              tmpl = replacePlaceholders(typeof match === 'object' ? match.text : match);
              subjectTemplateRef.current = tmpl;
            }
          }
          return tmpl ? { subjectLineTemplate: tmpl } : {};
        })(),
        // Precompute 4 brand-voice subject alternatives during body-gen so the
        // ⚡ button opens instantly on first click. Skipped when replying to a
        // previous email (subject is inherited from the thread).
        precomputeSubjectAlternatives: !isReplyToPrevious,
        // Static subject variants (no {instruction}) — used ONLY as brand-voice
        // reference so the suite matches campaign tone. Not shown to the user.
        staticSubjectLinesForVoice: (staticSubjectLines || [])
          .map(sl => (sl && typeof sl === 'object' ? sl.text : sl))
          .filter(t => typeof t === 'string' && t.trim() && !hasInstructionBlocks(t))
          .map(t => replacePlaceholders(t)),
      };

      // Follow-up context: give the writer the email we last sent this contact
      // so it can match style/content and not repeat the same pitch verbatim.
      const topicType = topicTypeMapping[topic];
      if (isFollowUpTopicType(topicType)) {
        const previousEmail = await fetchPreviousSentEmail(
          userId,
          rowData,
          [selectedEmail, ...emailAddresses].filter(Boolean)
        );
        if (previousEmail) {
          requestData.previousEmail = previousEmail;
        }
      }

      // Füge soziale Profil-URLs hinzu, falls vorhanden
      if (rowData.instagram_url) {
        requestData.instagram = rowData.instagram_url;
      }
      
      if (rowData.facebook_url) {
        requestData.facebook = rowData.facebook_url;
      }
      
      if (rowData.tiktok_url) {
        requestData.tiktok = rowData.tiktok_url;
      }

      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`E-Mail-Generierung fehlgeschlagen: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setBodyContent(data.emailContent);
      triggerAiImages(data.emailContent);

      if (data.smartSubjectLine && !isReplyToPrevious) {
        subjectTemplateRef.current = null;
        const validStaticLines = (staticSubjectLines || []).filter(sl => {
          const text = typeof sl === 'object' ? sl.text : sl;
          return text && text.trim();
        });

        if (validStaticLines.length > 0) {
          const pool = [
            { text: data.smartSubjectLine, id: 'smart_subject' },
            ...validStaticLines
              .filter(sl => {
                const raw = typeof sl === 'object' ? sl.text : sl;
                return !hasInstructionBlocks(raw);
              })
              .map(sl => {
                const raw = typeof sl === 'object' ? sl.text : sl;
                return { text: replacePlaceholders(raw), id: typeof sl === 'object' ? sl.id : null };
              }).filter(sl => sl.text)
          ];
          if (data.resolvedSubjectLine) {
            pool.push({ text: data.resolvedSubjectLine, id: 'resolved_subject' });
          }
          const pick = pool[Math.floor(Math.random() * pool.length)];
          console.log(`🎲 [SUBJECT-POOL] Pool size: ${pool.length}, picked:`, pick);
          setSubjectLine(pick.text);
          setTrackingData(prev => ({ ...prev, subjectLineId: pick.id }));
        } else {
          setSubjectLine(data.smartSubjectLine);
          setTrackingData(prev => ({ ...prev, subjectLineId: 'smart_subject' }));
        }
        if (data.resolvedSubjectAlternatives && data.resolvedSubjectAlternatives.length > 0) {
          setSubjectAlts(data.resolvedSubjectAlternatives);
          setSubjectAltsPrecomputed(true);
        } else if (data.subjectAlternatives && data.subjectAlternatives.length > 0) {
          setSubjectAlts(data.subjectAlternatives);
          setSubjectAltsPrecomputed(true);
        }
      } else if (data.resolvedSubjectLine && !isReplyToPrevious) {
        console.log('📝 [SUBJECT-INSTR] Using resolved subject from backend:', data.resolvedSubjectLine);
        setSubjectLine(data.resolvedSubjectLine);
        subjectTemplateRef.current = null;
        if (data.resolvedSubjectAlternatives && data.resolvedSubjectAlternatives.length > 0) {
          setSubjectAlts(data.resolvedSubjectAlternatives);
          setSubjectAltsPrecomputed(true);
        }
      } else if (subjectTemplateRef.current && !isReplyToPrevious) {
        console.warn('📝 [SUBJECT-INSTR] Resolution failed — stripping instruction blocks from subject');
        setSubjectLine(stripInstructionBlocks(subjectTemplateRef.current));
        subjectTemplateRef.current = null;
      } else if ((!subjectLine || subjectLine.trim() === '') && data.subjectLine) {
        setSubjectLine(data.subjectLine);
      }

      // Precomputed brand-voice alternatives for the ⚡ button (static-template
      // case: no smart subject, no {instruction} — backend still ran the suite).
      // Only apply when we didn't already set alts from resolved/smart flow above.
      if (
        !isReplyToPrevious
        && data.subjectAlternatives
        && data.subjectAlternatives.length > 0
        && !data.smartSubjectLine
        && !data.resolvedSubjectAlternatives
      ) {
        setSubjectAlts(data.subjectAlternatives);
        setSubjectAltsPrecomputed(true);
      }
    } catch (error) {
      console.error('Fehler bei der E-Mail-Generierung:', error);
      // An empty balance is not a generation bug — send the user to the dialog
      // that says so, instead of "please try again" (which would never work).
      if (
        error?.code === 'insufficient_credits'
        || /not enough credits/i.test(error?.message || '')
      ) {
        openCreditsModal(emailGenerationCost({}));
      } else {
        // Voice-Clone errors carry a user-facing message (vcUserMessage) built
        // from the backend code/details — prefer it so the user sees exactly
        // what to fix (e.g. "no active variant"), otherwise show the generic copy.
        alert(error?.vcUserMessage || 'Die E-Mail konnte nicht generiert werden. Bitte versuche es erneut.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const contactId = rowData?.id || rowData?.lead_id || rowData?.business_name;
  useEffect(() => {
    setWritingSparks([]);
    setWriterDirections([]);
    setActiveDirection(null);
    setActiveCategory(null);
    setCachedBriefing(null);
    setSparksGenCount(0);
    setSparksCopied(null);
    setExpandingIdx(null);
    usedAnglesRef.current = [];
    digestHashRef.current = null;
  }, [contactId, selectedTopic]);

  useEffect(() => {
    if (sidePanelOpen && bodyContent && writingSparks.length === 0 && !sparksLoading) {
      fetchWritingSparks(bodyContent);
    }
  }, [sidePanelOpen, bodyContent]);

  // Build creativity-dependent prompt for perfect mode
  const getPerfectPrompt = (level) => {
    if (level <= 2) return `Take the existing email content and only fix typos, grammar errors, and minor phrasing issues. Keep the text almost identical — same structure, same words, same tone. Only correct obvious mistakes.`;
    if (level <= 4) return `Take the existing email content and polish it. Fix any errors, improve word choices, and enhance clarity while keeping the same structure, tone, and core message. Small refinements only — the email should still feel very similar to the original.`;
    if (level <= 6) return `Take the existing email content and improve it noticeably. Rephrase sentences for better flow, enhance professionalism, and make it more engaging. Keep the core message and intent, but feel free to restructure sentences and improve the overall quality significantly.`;
    if (level <= 8) return `Take the existing email content and substantially rewrite it. Use the same key information and intent, but create a fresh, compelling version with new phrasing, better structure, and a more engaging approach. The result should feel like a new email covering the same points.`;
    return `Use the existing email only as a source of information (recipient, topic, intent). Write a completely new, creative email from scratch based on these details. New structure, new angle, new wording — make it stand out and feel entirely fresh.`;
  };

  // E-Mail perfektionieren (bestehenden Text verfeinern)
  const handlePerfectEmail = async (creativityLevel) => {
    if (isPerfecting || !bodyContent || bodyContent.trim() === "") {
      console.log('📧 [NORMAL-PERFECT] Cannot perfect - already perfecting or no content');
      return;
    }
    
    const level = creativityLevel ?? perfectCreativity;
    setIsPerfecting(true);
    setBodyContent(''); // Text sofort entfernen für Loading-Animation
    
    try {
      console.log('📧 [NORMAL-PERFECT] Perfecting email for:', rowData.business_name, '| creativity:', level);
      
      // KI-Modellparameter — Template-spezifische Settings bevorzugen (wie bei normaler Generierung)
      const currentTemplate = Object.values(userTopics || {}).find(t => t.topic === selectedTopic);
      const model = currentTemplate?.model || aiSettings.model || 'gpt-4.1-mini';
      const maxTokens = currentTemplate?.maxTokens || parseInt(aiSettings.max_tokens || aiSettings.maxTokens) || 500;
      const language = currentTemplate?.language || aiSettings.language || 'auto';
      
      const requestData = {
        topic: "Perfect Email",
        context: `Current email content: ${bodyContent}`,
        businessName: rowData.business_name || '',
        firstName: getFirstDmName(rowData),
        senderName: userSettings.user_name || '',
        senderEmail: selectedSenderEmail,
        textSignature: accountTextSignature(senderEmails.find(a => a.email === selectedSenderEmail)),
        topicSpecificNote: getPerfectPrompt(level),
        model,
        maxTokens,
        language,
        perfectMode: true
      };
      
      // API-Call zur Perfektionierung
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        throw new Error(`API-Fehler: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.emailContent) {
        // Setze den perfektionierten Inhalt
        setBodyContent(data.emailContent);
        
        console.log('📧 [NORMAL-PERFECT] ✅ Email successfully perfected');
      } else {
        throw new Error('Unerwartetes API-Antwortformat');
      }
      
    } catch (error) {
      console.error('📧 [NORMAL-PERFECT] Error perfecting email:', error);
      // Bei Fehler bleibt der ursprüngliche Text erhalten
    } finally {
      setIsPerfecting(false);
    }
  };

  const fetchWritingSparks = async (emailText, directionPrompt, category, position = 'top') => {
    if (sparksLockRef.current) return;
    sparksLockRef.current = true;
    setSparksLoading(true);
    setSparksLoadingPos(writingSparks.length > 0 ? position : null);
    let creditDeducted = false;
    try {
      if (sparksGenCount > 0) {
        try {
          const deductCreditsFn = httpsCallable(functions, 'deductCredits');
          await deductCreditsFn({
            action: 'salesgod_writer',
            amount: costOf('emailSalesgodWriter'),
            worldId: userId,
            metadata: { type: directionPrompt ? 'direction' : category ? 'category' : 'refresh', category: category || null, contact: rowData?.business_name || '' },
          });
          creditDeducted = true;
        } catch (creditErr) {
          console.error('Credit deduction failed:', creditErr);
          setSparksLoading(false);
          setSparksLoadingPos(null);
          sparksLockRef.current = false;
          alert('Nicht genügend Credits. Bitte lade deine Credits auf.');
          return;
        }
      }
      setSparksGenCount(prev => prev + 1);

      const recipient = getRecipientInfoForContact(rowData, selectedEmail);
      const igInsight = rowData.researchIntelligence?.instagramInsight;
      const igPosts = rowData.researchIntelligence?.instagramPosts;
      const ri = rowData.researchIntelligence || {};

      const templateData = getTopicSpecificNoteWithTracking();

      const sparksIgParts = [];
      if (igInsight) {
        sparksIgParts.push(`[IG RESEARCH] Instagram (@${igInsight.handle}): ${igInsight.summary}`);
        if (igInsight.uniqueFacts?.length) sparksIgParts.push(`Key facts: ${igInsight.uniqueFacts.join('; ')}`);
        if (igInsight.topics?.length) sparksIgParts.push(`Their topics: ${igInsight.topics.join(', ')}`);
        if (igInsight.targetAudience) sparksIgParts.push(`Target audience: ${igInsight.targetAudience}`);
        if (igInsight.painPoints?.length) sparksIgParts.push(`Pain points: ${igInsight.painPoints.join('; ')}`);
        if (igInsight.sellingPoints?.length) sparksIgParts.push(`Their USPs: ${igInsight.sellingPoints.join('; ')}`);
        if (igInsight.salesAngle) sparksIgParts.push(`Sales angle: ${igInsight.salesAngle}`);
        if (igInsight.activityScore != null) sparksIgParts.push(`Activity: ${igInsight.activityScore}/10, Engagement: ${igInsight.engagementScore ?? '?'}/10${igInsight.engagementTrend && igInsight.engagementTrend !== 'stable' ? ` (${igInsight.engagementTrend})` : ''}`);
        if (Array.isArray(igPosts) && igPosts.length > 0) {
          const recentPosts = igPosts.filter(p => p.text).slice(0, 3).map((p, i) => {
            const excerpt = p.text.length > 120 ? p.text.substring(0, 120) + '...' : p.text;
            const stats = [p.like_count ? `${p.like_count} likes` : '', p.comment_count ? `${p.comment_count} comments` : ''].filter(Boolean).join(', ');
            return `  ${i + 1}. "${excerpt}"${stats ? ` (${stats})` : ''}`;
          });
          if (recentPosts.length > 0) sparksIgParts.push(`Recent posts:\n${recentPosts.join('\n')}`);
        }
      }

      const sparksWaParts = [];

      const richContext = [
        composeAiContext(),
        rowData.lead_website ? `Website: ${rowData.lead_website}` : '',
        ri.companyInfo ? `Company intel: ${ri.companyInfo}` : '',
        ri.industryFit ? `Industry fit: ${ri.industryFit}` : '',
        ri.leadScore ? `Lead score: ${ri.leadScore} (${ri.leadRating || ''})` : '',
        ri.budgetIndicators ? `Budget indicators: ${ri.budgetIndicators}` : '',
        ri.reasoning ? `Research reasoning: ${ri.reasoning}` : '',
        ri.companySizeEstimate ? `Company size: ${ri.companySizeEstimate}` : '',
        rowData.lead_instagram_followers ? `Instagram followers: ${rowData.lead_instagram_followers}` : '',
        ...sparksIgParts,
        ...sparksWaParts,
        rowData.lead_crawl_summary ? `Website summary: ${rowData.lead_crawl_summary}` : '',
      ].filter(Boolean).join('\n');

      // CLONE-AWARE: voice-clone templates keep their campaign brief in cloneConfig,
      // not in generalInfo/topicSpecificNote. Feed that brief to the Writer as its
      // "sender offer" so the angle inventory + offer bridge actually reflect the
      // campaign, and pass cloneId so the backend can fold in the trained voice + taboos.
      const isCloneTemplate = isVoiceCloneTopicData(templateData);
      const cloneBrief = isCloneTemplate ? buildCloneWriterBrief(templateData.cloneConfig) : '';
      const effectiveGeneralInfo = isCloneTemplate
        ? [templateData.generalInfo || '', cloneBrief].filter(Boolean).join('\n')
        : (templateData.generalInfo || '');

      // Digest reuse: build the rich digest ONCE per (context + email + offer). If the
      // hash still matches, reuse the cached digest (no re-analysis = lean credits) and
      // just push the ledger of already-used angles so we get FRESH angles each time.
      const digestHash = hashWriterContext(richContext, emailText || '', effectiveGeneralInfo, templateData.text || '', isCloneTemplate ? (templateData.cloneConfig?.cloneId || '') : '');
      const hashMatches = digestHashRef.current === digestHash;
      const reuseDigest = hashMatches && cachedBriefing && cachedBriefing.angleInventory;
      if (!hashMatches) {
        // Context changed → previous angles no longer apply.
        usedAnglesRef.current = [];
      }

      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.generateWritingSparks(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: rowData.business_name || '',
          website: rowData.lead_website || '',
          firstName: getFirstDmName(rowData),
          recipientName: recipient?.name || '',
          recipientRole: recipient?.role || '',
          senderName: userSettings.user_name || '',
          context: richContext,
          currentEmailText: emailText || '',
          topic: selectedTopic || '',
          language: templateData.language || aiSettings.language || 'auto',
          generalInfo: effectiveGeneralInfo,
          templateNote: templateData.text || '',
          companyName: userSettings.company_name || '',
          direction: directionPrompt || null,
          pacing: rowData?.lead_pacing || null,
          cachedBriefing: reuseDigest ? cachedBriefing : null,
          usedAngles: usedAnglesRef.current,
          category: category || null,
          persona: activePersona || null,
          cloneId: isCloneTemplate ? (templateData.cloneConfig?.cloneId || null) : null,
          userId: userId || null,
        })
      });
      const data = await response.json();
      const icebreakerSparks = (data.icebreakers || []).map((s, i) => ({ ...s, type: 'icebreaker', batch: 0, colorIdx: i % 4 }));
      const regularSparks = (data.sparks || []).map((s, i) => ({ ...s, colorIdx: i % 4, categoryTag: category || null }));
      const allNewSparks = [...icebreakerSparks, ...regularSparks];
      if (allNewSparks.length > 0) {
        if (writingSparks.length > 0) {
          const dirLabel = directionPrompt ? (writerDirections.find(d => d.prompt === directionPrompt)?.label || directionPrompt) : null;
          const catLabel = category ? WRITER_CATEGORIES.find(c => c.id === category)?.label : null;
          const taggedNew = allNewSparks.map(s => ({ ...s, directionLabel: dirLabel || catLabel || null }));
          if (position === 'bottom') {
            setWritingSparks(prev => [...prev, ...taggedNew]);
          } else {
            const taggedOld = writingSparks.map(s => s.previous ? s : { ...s, previous: true });
            setWritingSparks([...taggedNew, ...taggedOld]);
          }
        } else {
          setWritingSparks(allNewSparks);
        }
      }
      if (data.briefing) {
        setCachedBriefing(data.briefing);
        digestHashRef.current = digestHash;
      }
      // Advance the angle ledger with whatever angles were just shown, so the next
      // generation avoids them and the user keeps seeing fresh hooks.
      const newAngleIds = allNewSparks.map(s => s.angleId).filter(Boolean);
      if (newAngleIds.length > 0) {
        usedAnglesRef.current = Array.from(new Set([...usedAnglesRef.current, ...newAngleIds]));
      }
      if (data.directions?.length && !directionPrompt) {
        setWriterDirections(data.directions);
      }
    } catch (err) {
      console.error('SalesGod Writer error:', err);
      if (creditDeducted) {
        try {
          const deductCreditsFn = httpsCallable(functions, 'deductCredits');
          await deductCreditsFn({ action: 'salesgod_writer_refund', amount: -costOf('emailSalesgodWriter'), worldId: userId });
        } catch (_) { /* best effort */ }
      }
    } finally {
      setSparksLoading(false);
      setSparksLoadingPos(null);
      sparksLockRef.current = false;
    }
  };

  const expandSpark = async (index) => {
    if (expandLockRef.current) return;
    const parentSpark = writingSparks[index];
    if (!parentSpark) return;
    expandLockRef.current = true;
    let creditDeducted = false;
    if (sparksGenCount > 0) {
      try {
        const deductCreditsFn = httpsCallable(functions, 'deductCredits');
        await deductCreditsFn({
          action: 'salesgod_writer_expand',
          amount: costOf('emailSalesgodExpand'),
          worldId: userId,
          metadata: { contact: rowData?.business_name || '' },
        });
        creditDeducted = true;
      } catch (creditErr) {
        console.error('Credit deduction failed:', creditErr);
        expandLockRef.current = false;
        alert('Nicht genügend Credits. Bitte lade deine Credits auf.');
        return;
      }
    }
    setSparksGenCount(prev => prev + 1);
    setExpandingIdx(index);
    try {
      const templateData = getTopicSpecificNoteWithTracking();
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.generateWritingSparks(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expandFromSentence: parentSpark.text,
          language: templateData.language || aiSettings.language || 'auto',
          currentEmailText: bodyContent || '',
          templateNote: templateData.text || '',
          generalInfo: templateData.generalInfo || '',
          persona: activePersona || null,
        })
      });
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      if (data.sparks?.length) {
        const newCards = data.sparks.map(s => ({ ...s, expanded: true, colorIdx: parentSpark.colorIdx, ...(parentSpark.previous && { previous: true }) }));
        setWritingSparks(prev => {
          const copy = [...prev];
          copy.splice(index + 1, 0, ...newCards);
          return copy;
        });
      } else {
        setSparksCopied('__expand_failed__');
        setTimeout(() => setSparksCopied(null), 2000);
      }
    } catch (err) {
      console.error('SalesGod Writer expand error:', err);
      setSparksCopied('__expand_failed__');
      setTimeout(() => setSparksCopied(null), 2000);
      if (creditDeducted) {
        try {
          const deductCreditsFn = httpsCallable(functions, 'deductCredits');
          await deductCreditsFn({ action: 'salesgod_writer_refund', amount: -costOf('emailSalesgodExpand'), worldId: userId });
        } catch (_) { /* best effort */ }
      }
    } finally {
      setExpandingIdx(null);
      expandLockRef.current = false;
    }
  };

  // Kontaktstatus aktualisieren
  const updateContactStatus = async (userId, leadId, newStatus) => {
    if (!userId || !leadId || !newStatus) {
      return false;
    }
    try {
      const timestamp = Date.now();

      // Optimistisches UI-Update
      if (onEmailResult) {
        const statusUpdateInfo = {
          id: leadId,
          field: 'lead_contacted',
          value: newStatus,
          timestamp,
          _optimistic_update: true,
          _prevent_refresh: true,
          _critical_update: true,
        };
        onEmailResult('status-update', statusUpdateInfo);

        onEmailResult('status-update', {
          id: leadId,
          field: 'lead_status',
          value: newStatus,
          timestamp,
          _optimistic_update: true,
        });
      }

      const updateData = {
        lead_contacted: newStatus,
        lead_status: newStatus,
        last_contact_date: new Date().toISOString(),
        updated_at: timestamp,
        _optimistic_update: true,
        _client_update_timestamp: timestamp,
      };

      if (newStatus !== 'Not Contacted') {
        updateData.lead_last_email = {
          timestamp,
          status: newStatus,
          recipientEmail: selectedEmail,
          ...(rowData.leadSignals ? { leadSignalsSnapshot: minimalSignalsSnapshot(rowData.leadSignals) } : {}),
        };
      }

      const contactDocRef = doc(firestore, `World/${userId}/db/${leadId}`);
      await updateDoc(contactDocRef, updateData);
      logStatusChange(firestore, userId, leadId, rowData?.lead_contacted || rowData?.lead_status, newStatus, 'after sending an email');

      // Warte kurz auf den bestätigten Wert, um eventuelle Optimismus-Mismatches auszubügeln
      waitForServerConfirmation(userId, leadId, 'lead_contacted', confirmed => {
        onEmailResult('status-update', {
          id: leadId,
          field: 'lead_contacted',
          value: confirmed.lead_contacted,
          timestamp: confirmed.updated_at || Date.now()
        });
        onEmailResult('status-update', {
          id: leadId,
          field: 'lead_status',
          value: confirmed.lead_status,
          timestamp: confirmed.updated_at || Date.now()
        });
      });

      return true;
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Kontaktstatus:', error);
      return false;
    }
  };

  // Letzten Absender in der Datenbank speichern
  const saveLastSenderToDatabase = async (senderEmail) => {
    try {
      if (!userId || !senderEmail) return false;
      
      const lastSenderRef = ref(database, `/World/${userId}/settings/lastUsedSender`);
      await set(lastSenderRef, {
        email: senderEmail,
        timestamp: Date.now(),
        updatedAt: new Date().toISOString()
      });
      
      return true;
    } catch (error) {
      console.error('Fehler beim Speichern des letzten Absenders:', error);
      return false;
    }
  };

  // AI Tag basierend auf Topic setzen
  const setAiTagBasedOnTopic = async (topic, contactId) => {
    try {
      // Erweiterte Suche: Prüfe sowohl in selectedTopics als auch in customTopics
      let matchingTopic = null;
      
      // 1. Suche in allen userTopics (sowohl selectedTopics als auch customTopics)
      matchingTopic = Object.values(userTopics).find(topicData => {
        return topicData.topic === topic || topicData.type === topic;
      });
      
      // 2. Falls nicht gefunden, erweiterte Suche in der Struktur
      if (!matchingTopic && userTopics.selectedTopics) {
        matchingTopic = Object.values(userTopics.selectedTopics).find(topicData => {
          return topicData.topic === topic || topicData.type === topic;
        });
      }
      
      // 3. Falls immer noch nicht gefunden, suche in customTopics
      if (!matchingTopic && userTopics.customTopics) {
        matchingTopic = Object.values(userTopics.customTopics).find(topicData => {
          return topicData.topic === topic || topicData.type === topic;
        });
      }
      
      if (!matchingTopic) {
        return false;
      }
      
      // Prüfe, ob das Topic einen zugeordneten AI Tag hat
      if (!matchingTopic.aiTagId) {
        return false;
      }
      
      // Lade die AI Tag-Einstellungen
      const aiTagRef = ref(database, `/World/${userId}/settings/aiTags/${matchingTopic.aiTagId}`);
      const aiTagSnapshot = await new Promise((resolve) => {
        onValue(aiTagRef, resolve, { onlyOnce: true });
      });
      
      if (!aiTagSnapshot.exists()) {
        return false;
      }
      
      const aiTagData = aiTagSnapshot.val();
      
      // Erstelle den neuen Tag für den Kontakt
      const newTag = {
        tag: aiTagData.tag,
        name: aiTagData.name,
        color: aiTagData.color,
        icon: aiTagData.icon,
        appliedAt: new Date().toISOString(),
        appliedBy: 'email_send',
        appliedTopic: topic,
        ...(aiTagData.selectedTopic && { selectedTopic: aiTagData.selectedTopic })
      };
      
      const contactDocRef = doc(firestore, `World/${userId}/db/${contactId}`);

      if (aiTagData.overwriteOtherTags === true) {
        await updateDoc(contactDocRef, { tags: { [matchingTopic.aiTagId]: newTag } });
      } else {
        const contactSnap = await getDoc(contactDocRef);
        const existingTags = contactSnap.exists() ? contactSnap.data().tags || {} : {};
        await updateDoc(contactDocRef, { [`tags.${matchingTopic.aiTagId}`]: newTag, tags: { ...existingTags, [matchingTopic.aiTagId]: newTag } });
      }
      
      return true;
      
    } catch (error) {
      console.error('🏷️ [AI-TAG-SETTER] Error setting AI tag:', error);
      return false;
    }
  };

  // E-Mail senden
  const handleSend = async () => {
    // ⛔ Image guard: don't send while an AI image is still generating (covers
    // keyboard shortcuts that bypass the disabled button).
    if (Object.values(aiImages).some((v) => v && v.status === "loading")) {
      onEmailResult('error', 'Please wait — an AI image is still being generated.');
      return;
    }

    // Validate the actually selected recipient — NOT rowData.lead_email. The
    // recipient can be a decision-maker email (selectedEmail) even when the
    // contact has no primary lead_email, and that is exactly what we send to
    // below (recipients: [currentSelectedEmail, ...]).
    if (!selectedEmail || !selectedEmail.trim()) {
      console.error("No email address found for this contact");
      onEmailResult('error', 'No email address found for this contact');
      return;
    }

    // ⛔ Undeliverable guard: this exact address hard-bounced before. Blocked
    // client-side (mirrors the server gate) so a stale auto-selection can't slip
    // through. Other addresses of the contact stay usable.
    if (isEmailUndeliverable(selectedEmail)) {
      onEmailResult('error', 'This address previously bounced (undeliverable) and cannot be emailed. Pick another address.');
      return;
    }

    // 🔴 Re-send guard: this exact address already received an intro/follow-up.
    // Demand an explicit extra confirmation before mailing it a second time.
    // (A different, not-yet-contacted address on the same domain is unaffected.)
    if (!resendConfirmedRef.current && isResendBlocked(selectedEmail)) {
      setResendConfirmOpen(true);
      return;
    }
    resendConfirmedRef.current = false;

    // 🟡 Same-step guard: only a repeated follow-up stage or template confirms.
    // Intro already sent, then the first follow-up, does not.
    if (
      !followUpConfirmedRef.current
      && followUpDuplicates.length > 0
    ) {
      setFollowUpConfirmOpen(true);
      return;
    }
    followUpConfirmedRef.current = false;

    // 📎 Attachments must be fully uploaded before we hand off to the backend.
    if (EmailAttachmentBar.hasPending(attachments)) {
      onEmailResult('error', 'Please wait for attachments to finish uploading.');
      return;
    }
    if (EmailAttachmentBar.hasErrors(attachments)) {
      onEmailResult('error', 'One or more attachments failed to upload. Remove them or retry.');
      return;
    }

    const currentId = rowData.id;
    const currentContent = bodyContent;
    const currentSubject = subjectLine;
    const currentSelectedEmail = selectedEmail;
    const currentCcEmails = [...ccEmails];
    const currentSenderEmail = selectedSenderEmail;
    const currentBusinessName = rowData.business_name || rowData.lead_name || 'Unknown Contact';
    
    // 📧 Tracking-Daten vor dem Schließen speichern
    const currentTrackingData = { ...trackingData };
    const currentTopic = selectedTopic;

    setIsSending(true);
    
    // Neuen Kontaktstatus bestimmen.
    // Spiegelt 1:1 das Backend (WorldEmailSender.js): jeder Nicht-Reply-Send setzt
    // einen Status, mit 'Introduced' als Default für Custom-/umbenannte Topics.
    // Vorher deckte das Frontend nur 4 fest verdrahtete Topic-Namen ab, weshalb
    // Custom-Topics keinen optimistischen Patch bekamen und der Lead-Status erst
    // nach einem Page-Refresh erschien.
    const currentEmailType = topicTypeMapping[selectedTopic] || resendDraft?.emailType || "unknown_topic_type";
    let newStatus = null;
    if (!isReply) {
      const emailTypeForStatus = currentEmailType;
      if (emailTypeForStatus !== 'email_reply') {
        switch (emailTypeForStatus) {
          case 'follow_up_1': newStatus = 'First Follow-up'; break;
          case 'follow_up_2': newStatus = 'Second Follow-up'; break;
          case 'follow_up_3': newStatus = 'Third Follow-up'; break;
          default: newStatus = 'Introduced';
        }
      }

      // Kontaktstatus aktualisieren
      if (newStatus) {
        await updateContactStatus(userId, currentId, newStatus);
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    
    // E-Mail im Hintergrund senden — Popup bleibt offen bis User es schließt
    (async () => {
      try {
        // Absender-E-Mail finden
        const senderEmailData = senderEmails.find(item => item.email === currentSenderEmail);
        
        if (!senderEmailData) {
          throw new Error("The selected sender email was not found.");
        }
        
        // Resolve gegen stale React-State (Race zwischen Firestore/RTDB-Listenern).
        // Bei Read-Error fällt der Helper transparent auf den bestehenden State zurück
        // → der Send wird durch diesen Schritt nie blockiert.
        const { footerId: resolvedFooterId, footerSettings: resolvedFooterSettings } = await resolveFooterForSend({
          userId,
          senderAccountId: senderEmailData.id,
          fallbackFooterId: senderEmailData.footerId || null,
          fallbackFooterSettings: userSettings,
        });

        // Merge freshly generated per-recipient AI images into the settings so
        // {{image:token}} resolves to the generated image (backend then inlines
        // it as CID like any other email_body_images/ asset).
        const readyAiImages = Object.fromEntries(
          Object.entries(aiImages).filter(([, v]) => v && v.status === "ready" && v.imageUrl)
        );
        // Resolve {{variables}} in image alt texts per recipient so no two sends
        // share an identical (or placeholder-leaking) alt attribute.
        const settingsForHtml = withResolvedImageAltText(
          withResolvedAiImages(resolvedFooterSettings, readyAiImages),
          buildImagePromptVariables(rowData, { userSettings, selectedEmail })
        );

        // E-Mail formatieren mit account-spezifischem Footer
        let formattedContent = convertToHtml(currentContent, settingsForHtml, resolvedFooterId);

        if (screenComposerImage && screenComposerImage.url) {
          formattedContent += `<div style="margin: 20px 0; text-align: center;"><img src="${screenComposerImage.url}" alt="" style="max-width: 100%; border-radius: 8px; display: inline-block;" /></div>`;
        }

        let contentWithFooter = addFooterToEmail(formattedContent, resolvedFooterSettings, resolvedFooterId);
        
        // Request-Daten erstellen
        const requestData = {
          recipients: [currentSelectedEmail, ...currentCcEmails],
          cc: currentCcEmails,
          subject: currentSubject || '',
          content: contentWithFooter || '',
          userId: userId,
          businessName: currentBusinessName,
          leadId: currentId,
          emailType: currentEmailType,
          ...(resendDraft ? { safeGuardApproved: true } : {}),
          senderEmail: senderEmailData.email.trim(),
          senderPassword: senderEmailData.password,
          senderName: senderEmailData.sender_name || userSettings.user_name || 'BonusClub',
          imapHost: senderEmailData.imap_host,
          imapPort: senderEmailData.imap_port,
          smtpHost: senderEmailData.smtp_host,
          smtpPort: senderEmailData.smtp_port,
          encryptionType: senderEmailData.encryption_type,
          smtpEncryption: String(senderEmailData.smtp_encryption),
          emailId: senderEmailData.id,
          // 📎 ATTACHMENTS: only metadata; backend pulls bytes from Storage right before send
          attachments: EmailAttachmentBar.toRequestPayload(attachments),
          // 📧 EMAIL TRACKING: Send tracking data to backend
          templateId: currentTrackingData.templateId,
          subjectLineId: currentTrackingData.subjectLineId,
          instructionId: currentTrackingData.instructionId,
          // Voice-Clone-mode sends carry a variantId so the backend can
          // credit per-structure-variant stats. Standard-mode sends leave
          // it null and the backend's variantStats branch is skipped.
          variantId: currentTrackingData.variantId || null,
          topicName: currentTopic,
          // 🔒 AUDIT: Wer hat die E-Mail gesendet (Team-Member-Tracking)
          sentByUserId: currentUserId
        };
        
        // E-Mail senden
        const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.sendWorldEmail(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });
        
        const responseText = await response.text();
        
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          console.error('Fehler beim Parsen der JSON-Antwort:', e);
          if (onEmailResult) {
            onEmailResult('error', `Server response is not valid JSON: ${responseText.substring(0, 100)}...`, rowData);
          }
          return;
        }
        
        if (data.success) {
          // 🛡️ [SAFEGUARD] Prüfe, ob E-Mail von SafeGuard abgelehnt und als Draft gespeichert wurde
          if (data.drafted && data.safeGuardRating !== undefined) {
            setIsSending(false);
            if (onEmailResult) {
              onEmailResult(
                'safeguard_rejected', 
                `Email quality rating: ${data.safeGuardRating}/${data.minimumRating || 10}. Saved as draft for manual review.`, 
                rowData, 
                null, 
                currentSelectedEmail
              );
            }
            return;
          }

          // 📊 [PRE-SEND SCORER] Scored but blocked in strict mode
          if (data.drafted && data.preSendScore) {
            setIsSending(false);
            if (onEmailResult) {
              const s = data.preSendScore;
              onEmailResult(
                'scorer_rejected',
                `Pre-Send Score: ${s.compositeScore}/10 (Buyer: ${s.buyerValue}/10, Deliverability: ${s.deliverabilityRisk}/10). Saved as draft.`,
                rowData,
                null,
                currentSelectedEmail
              );
            }
            return;
          }
          
          // Erfolg – normale Sendung
          setSendSuccess(true);
          setIsSending(false);

          if (onEmailResult) {
            onEmailResult('success', `Email to ${currentBusinessName} successfully sent`, rowData, null, currentSelectedEmail, currentSenderEmail);
          }

          setTimeout(() => onClose(), 1200);

          // Fire-and-forget: couple pre-send score to analytics for closed-loop learning
          if (data.preSendScore && currentId) {
            coupleScoreToAnalytics(userId, currentId, data.preSendScore);
          }

          // Sofortiges optimistisches Status-Patch (gleichzeitig mit Toast)
          if (newStatus && onEmailResult) {
            const ts = Date.now();
            const patchPayload = (field) => ({
              id: currentId,
              field,
              value: newStatus,
              timestamp: ts,
              _optimistic_update: true,
              _prevent_refresh: true,
              _critical_update: true
            });

            onEmailResult('status-update', patchPayload('lead_contacted'));
            onEmailResult('status-update', patchPayload('lead_status'));
          }

          // Letzten Absender speichern
          await saveLastSenderToDatabase(currentSenderEmail);

          if (resendDraft?.draftId) {
            deleteDoc(doc(firestore, 'world_drafts', userId, 'emails', resendDraft.draftId)).catch(() => {});
          }

          // A draft prepared for this contact is spent now — the mail this popup
          // just sent replaces it. Backend does the same, this only spares the
          // Prepared Mails strip a round-trip before the card disappears.
          if (rowData?.has_prepared_email === true && currentId) {
            deleteDoc(doc(firestore, `World/${userId}/preparedEmails/${currentId}`)).catch(() => {});
            updateDoc(doc(firestore, `World/${userId}/db/${currentId}`), {
              has_prepared_email: false,
              prepared_email_at: deleteField(),
            }).catch(() => {});
          }

          // AI Tag basierend auf Topic setzen
          await setAiTagBasedOnTopic(currentTopic, currentId);

          if (rowData?.taskId && currentId) {
            completeTask(userId, { id: rowData.taskId, contactId: currentId }, { doneVia: "mail_sent" })
              .then(uids => {
                if (uids) window.dispatchEvent(new CustomEvent(CONTACT_PATCH_EVENT, { detail: { contactId: currentId, updates: { open_task_assignees: uids } } }));
              })
              .catch(err => console.warn('[Task] auto-complete after send failed:', err?.message || err));
          }

          // Auto-advance warmup flow if current step is an email node
          advanceFlowOnEmailSend(userId, currentId).then(flowResult => {
            if (flowResult.advanced && onEmailResult) {
              onEmailResult('flow-update', {
                id: currentId,
                warmupFlow: flowResult.newFlowState,
                flowEndStatus: flowResult.flowEndStatus,
                autoAppliedTags: flowResult.autoAppliedTags,
              });
            }
          });
        } else {
          setIsSending(false);
          // Opt-Out / Blacklist blocked
          if (data.optOutBlocked) {
            if (onEmailResult) {
              onEmailResult('blocked', `Contact is blocked or opted out. ${data.error || ''}`, rowData, null, currentSelectedEmail, currentSenderEmail);
            }
            return;
          }

          // CRM Domain Guard blocked (contact already lives in connected Close
          // CRM under a non-SalesGod / empty Lead Source). Backend returns
          // { blocked: true, blockType: 'crm_guard', error, reason, crmStatus,
          //   crmLeadSourceValue }. We surface a dedicated toast so users can
          // tell this apart from a generic send failure.
          if (data.blocked === true && data.blockType === 'crm_guard') {
            const sourceLabel = data.crmLeadSourceValue
              ? `Lead Source "${data.crmLeadSourceValue}"`
              : (data.crmStatus === 'crm_found_empty_source' ? 'empty Lead Source' : 'foreign Lead Source');
            const crmMessage = `Already in Close CRM (${sourceLabel}) — outreach blocked.`;
            if (onEmailResult) {
              onEmailResult('blocked', crmMessage, rowData, null, currentSelectedEmail, currentSenderEmail);
            }
            return;
          }

          // Fehler
          let errorMessage = data.error || 'Unknown error sending email';
          
          if (data.error && data.error.includes("Sender address rejected")) {
            errorMessage = `Die E-Mail-Adresse "${senderEmailData.email}" wurde vom Server abgelehnt.`;
          }
          
          if (onEmailResult) {
            onEmailResult('error', errorMessage, rowData);
          }
        }
      } catch (error) {
        setIsSending(false);
        console.error("Fehler beim Senden der E-Mail:", error);
        
        if (onEmailResult) {
          onEmailResult('error', error.message || 'Unknown error sending email', rowData);
        }
      }
    })();
  };

  // CC-Status für eine E-Mail umschalten
  const toggleCcEmail = (email) => {
    if (ccEmails.includes(email)) {
      setCcEmails(ccEmails.filter(e => e !== email));
    } else {
      if (email !== selectedEmail) {
        setCcEmails([...ccEmails, email]);
      }
    }
  };

  const handleVerifyDmEmail = async (entry) => {
    if (!entry || entry.source !== 'decision_maker' || !entry.email || !rowData?.id) return;
    const emailKey = entry.email.toLowerCase();
    if (checkingDmEmails[emailKey]) return;
    setCheckingDmEmails(prev => ({ ...prev, [emailKey]: true }));
    try {
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.validateVerifyEmail(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: entry.email }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const resultStatus = data?.result?.status;
      const isServiceError = ['service_error', 'unknown', 'antispam_system', 'smtp_protocol'].includes(resultStatus);
      if (!data?.success || isServiceError) {
        console.warn('[DM-EMAIL-CHECK] Inconclusive result, keeping email as-is:', resultStatus);
        setDmEmailOverrides(prev => ({ ...prev, [emailKey]: { checked: true, inconclusive: true } }));
        return;
      }
      const isStrictValid = data.result.isValid === true && resultStatus === 'ok';
      const isCatchAll = data.result.isCatchAll === true || resultStatus === 'ok_for_all';

      // Use effectiveRowData so locally added DMs are included
      const raw = effectiveRowData.lead_decision_makers;
      const dms = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!Array.isArray(dms)) throw new Error('Invalid decision makers payload');

      const dmIndex = dms.findIndex((dm) => (dm?.email || '').toLowerCase() === emailKey);
      if (dmIndex === -1) throw new Error('Decision maker not found');

      const updated = [...dms];
      if (isStrictValid) {
        updated[dmIndex] = {
          ...updated[dmIndex],
          emailChecked: true,
          emailVerified: true,
          emailValidationFailed: false,
          emailValidationStatus: 'valid',
        };
      } else if (isCatchAll) {
        updated[dmIndex] = {
          ...updated[dmIndex],
          emailChecked: true,
          emailVerified: false,
          emailValidationFailed: false,
          emailValidationStatus: 'catch_all',
          emailNote: 'catch-all domain — unverified, may bounce',
        };
      } else {
        updated[dmIndex] = {
          ...updated[dmIndex],
          email: null,
          emailChecked: true,
          emailVerified: false,
          emailValidationFailed: true,
          emailValidationStatus: 'invalid',
          emailSearched: true,
        };
      }

      const dmJson = JSON.stringify(updated);
      const contactRef = doc(firestore, `World/${userId}/db/${rowData.id}`);
      await updateDoc(contactRef, { lead_decision_makers: dmJson });
      // Clear local additions — Firestore now has the full list
      setLocalDmAdditions([]);
      const wasRemoved = !isStrictValid && !isCatchAll;
      logContactActivity(firestore, userId, rowData.id, {
        type: 'email_check',
        title: `Email checked: ${entry.email}`,
        detail: isStrictValid
          ? 'Valid'
          : isCatchAll
            ? 'Catch-all domain, unverified'
            : `Invalid (${String(resultStatus || 'unknown').replace(/_/g, ' ')}), removed from ${dms[dmIndex]?.name || 'the decision maker'}`,
      });
      setDmEmailOverrides(prev => ({ ...prev, [emailKey]: { checked: true, removed: wasRemoved, catchAll: isCatchAll } }));

      if (wasRemoved && selectedEmail && selectedEmail.toLowerCase() === emailKey) {
        const remaining = getAllContactEmails({ ...effectiveRowData, lead_decision_makers: dmJson });
        const fallback = remaining.find((e) => e.email && e.email.toLowerCase() !== emailKey);
        setSelectedEmail(fallback ? fallback.email : remaining[0]?.email || '');
      }
    } catch (error) {
      console.error('[DM-EMAIL-CHECK] Failed to validate DM email:', error);
    } finally {
      setCheckingDmEmails(prev => ({ ...prev, [emailKey]: false }));
    }
  };

  const handleSaveInlineDm = async () => {
    if (!inlineAddDm || !rowData?.id || savingDm) return;
    const name = (inlineAddDm.name || '').trim();
    const email = (inlineAddDm.email || '').trim();
    const role = (inlineAddDm.role || '').trim();
    if (!name && !email) return;
    setSavingDm(true);
    try {
      const newDm = { name, email, role };
      setLocalDmAdditions(prev => [...prev, newDm]);
      setInlineAddDm(null);
      if (email) {
        followUpRecipientTouchedRef.current = true;
        setSelectedEmail(email);
      }
      // Build full DM list: original + all previous local additions + new one
      const raw = rowData.lead_decision_makers;
      let baseDms = [];
      try {
        if (raw && typeof raw === 'string') baseDms = JSON.parse(raw);
        else if (Array.isArray(raw)) baseDms = raw;
      } catch (_) {}
      const allDms = [...baseDms, ...localDmAdditions, newDm];
      const dmJson = JSON.stringify(allDms);
      const contactRef = doc(firestore, `World/${userId}/db/${rowData.id}`);
      await updateDoc(contactRef, { lead_decision_makers: dmJson });
    } catch (error) {
      console.error('[ADD-DM] Failed to save decision maker:', error);
      setLocalDmAdditions(prev => prev.slice(0, -1));
    } finally {
      setSavingDm(false);
    }
  };

  // Direktes Aktualisieren des Betreffs
  const updateSubjectInputDirectly = (subject) => {
    setSubjectLine(subject);
    
    if (subjectInputRef.current) {
      subjectInputRef.current.value = subject;
      
      const event = new Event('input', { bubbles: true });
      subjectInputRef.current.dispatchEvent(event);
    }
  };

  // Prüfen, ob alle erforderlichen Felder ausgefüllt sind
  const isEmailValid = () => {
    return (
      selectedEmail && 
      selectedEmail.trim() !== '' && 
      selectedSenderEmail && 
      selectedSenderEmail.trim() !== '' && 
      subjectLine && 
      subjectLine.trim() !== '' && 
      bodyContent && 
      bodyContent.trim() !== ''
    );
  };

  // Block sending while any AI image for this email is still being generated,
  // so the recipient never gets a mail with an unfinished/placeholder image.
  const isGeneratingImages = Object.values(aiImages).some((v) => v && v.status === "loading");

  // Absender-E-Mail automatisch auswählen
  useEffect(() => {
    if (senderEmails.length === 0) return;

    const draftAccount = resendDraft?.senderEmail
      ? senderEmails.find((e) => (e.email || '').toLowerCase() === resendDraft.senderEmail.toLowerCase())
      : null;
    if (draftAccount) {
      setRandomSenderMode(false);
      setSelectedSenderEmail(draftAccount.email);
      return undefined;
    }

    let cancelled = false;

    // Flow override: when the contact is on a flow "Send Email" step with a
    // non-default sender mode, resolve (and persist) the flow sender instead of
    // the global lastUsedSender.
    const flowConfig = rowData?.flowSenderConfig;
    if (flowConfig && flowConfig.senderMode && flowConfig.senderMode !== 'default') {
      resolveFlowSenderEmail({
        userId,
        contactId: rowData?.id,
        flowConfig,
        senderEmails,
      }).then((flowEmail) => {
        if (cancelled) return;
        if (flowEmail) {
          setRandomSenderMode(false);
          setSelectedSenderEmail(flowEmail);
        } else {
          setSelectedSenderEmail(senderEmails[0].email);
        }
      }).catch(() => {
        if (!cancelled) setSelectedSenderEmail(senderEmails[0].email);
      });
      return () => { cancelled = true; };
    }

    // Default behaviour: use the last used sender.
    const lastSenderRef = ref(database, `/World/${userId}/settings/lastUsedSender`);
    const unsubscribe = onValue(lastSenderRef, (snapshot) => {
      if (snapshot.exists()) {
        const lastSender = snapshot.val();
        const matchingEmail = senderEmails.find(email => email.email === lastSender.email);
        if (matchingEmail) {
          setSelectedSenderEmail(matchingEmail.email);
          return;
        }
      }
      // Fallback auf die erste E-Mail
      setSelectedSenderEmail(senderEmails[0].email);
    }, { onlyOnce: true });

    return () => { cancelled = true; unsubscribe(); };
  }, [senderEmails, userId, rowData?.id, rowData?.flowSenderConfig, resendDraft?.senderEmail]);

  const waitForServerConfirmation = (userId, contactId, field, apply) => {
    try {
      const unsub = onSnapshot(
        doc(firestore, `World/${userId}/db/${contactId}`),
        snap => {
          if (snap.exists()) {
            const data = snap.data();
            apply(data);
            unsub();
          }
        },
        err => {
          console.error('Snapshot error:', err);
          unsub();
        }
      );
    } catch (e) {
      console.error('waitForServerConfirmation error', e);
    }
  };

  // Calendly-Link öffnen
  const handleOpenCalendly = () => {
    if (userSettings.calendlyBookingLink) {
      window.open(userSettings.calendlyBookingLink, '_blank');
    }
    setIsActionMenuOpen(false); // Menu nach Aktion schließen
  };

  // Calendly-Link kopieren
  const handleCopyCalendlyLink = async () => {
    if (userSettings.calendlyBookingLink) {
      try {
        await navigator.clipboard.writeText(userSettings.calendlyBookingLink);
        // Optionally show a success message
      } catch (err) {
        console.error('Failed to copy Calendly link:', err);
      }
    }
    setIsActionMenuOpen(false); // Menu nach Aktion schließen
  };

  // Action Menu togglen
  const toggleActionMenu = () => {
    setIsActionMenuOpen(!isActionMenuOpen);
  };

  // Action ausführen und Menu schließen
  const handlePerfectAndClose = () => {
    setPerfectSliderOpen(true);
    setIsActionMenuOpen(false);
  };

  const handleGenerateAndClose = () => {
    const note = regenCustomNote.trim();
    handleGenerate(undefined, note || undefined);
    setRegenCustomNote('');
    setIsActionMenuOpen(false);
  };

  const handleFrameworkRegenerate = (personalityId, frameworkId) => {
    const personality = emailPersonalities.find(p => p.id === personalityId);
    if (!personality) return;
    const framework = personality.frameworks.find(f => f.id === frameworkId);
    if (!framework) return;
    const prompt = `${personality.corePhilosophy}\n\n${framework.prompt}`;
    const note = regenCustomNote.trim();
    setFrameworkPickerOpen(false);
    setRegenCustomNote('');
    setIsActionMenuOpen(false);
    handleGenerate(prompt, note || undefined);
  };

  // Re-Generate a CopyTwin email with a specific (alternative) trained clone.
  // Passing the cloneId as override keeps the template untouched — it's only
  // used for this single generation.
  const handleCloneRegenerate = (cloneId) => {
    const note = regenCustomNote.trim();
    setFrameworkPickerOpen(false);
    setRegenCustomNote('');
    setIsActionMenuOpen(false);
    handleGenerate(undefined, note || undefined, cloneId || undefined);
  };

  // Standalone quick Re-Generate (next to the 3-dots menu) — one tap to open,
  // one tap on a CopyTwin to generate. No custom note, just a fast switch.
  const handleQuickCloneRegenerate = (cloneId) => {
    setCloneQuickOpen(false);
    handleGenerate(undefined, undefined, cloneId || undefined);
  };

  // Click outside to close the quick CopyTwin Re-Generate popover.
  useEffect(() => {
    if (!cloneQuickOpen) return undefined;
    const handleClickOutside = (event) => {
      if (regenQuickRef.current && !regenQuickRef.current.contains(event.target)) {
        setCloneQuickOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [cloneQuickOpen]);

  // Click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setIsActionMenuOpen(false);
        setFrameworkPickerOpen(false);
        setExpandedPersonality(null);
      }
    };

    if (isActionMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActionMenuOpen]);

  return (
    <div className={`emailAiPopupShell ${sidePanelOpen ? 'side-panel-open' : ''} ${rowData?.tags?.favorite ? 'is-favorite-popup' : ''}`}>
    <div className={`emailAiPopupContainer ${isReply ? 'reply-mode' : ''}`}>
      <div className="emailAiPopupHeader">
        <h2>AI E-Mail Generator</h2>
        <button className="emailAiPopupCloseButton" onClick={onClose}>×</button>
      </div>
      
      <div className="emailAiPopupContent">
        {/* LINKE SPALTE: Topics, Style, Context */}
        <div className="emailAiPopupLeftColumn">
          <div className="emailAiPopupBusinessHeader">
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <img style={{width: '34px', height: '34px'}} src="/img/flieger.webp" alt="Business" className="emailAiPopupBusinessIcon" />
              <div className="emailAiPopupBusinessName">
                <div style={{display: 'flex', textAlign: 'left', justifyContent: 'center', gap: '0px', flexDirection: 'column'}}>
                  <p style={{fontSize: '10px', color: '#9aa8b6', textAlign: 'left', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: '600', margin: 0}}>
                    {({
                      "Introduce Yourself": "Send Introduction to",
                      "Follow Up": "Send First Follow-Up to",
                      "Second Follow Up": "Send Second Follow-Up to",
                      "Third Follow Up": "Send Third Follow-Up to",
                    }[selectedTopic]) || "Send Mail to"}
                  </p>
                  <p style={{fontSize: '15px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px', margin: 0}}>
                    {rowData.business_name || rowData.lead_name || 'Unknown Contact'}
                    {rowData.tags?.favorite && (
                      <span className="nm-favorite-tag"><i className="fa fa-heart"></i> Favorite</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
            {rowData.lead_type && (
              <div className="emailAiPopupBusinessType">
                {rowData.lead_type}
              </div>
            )}
          </div>
          <div className="emailAiPopupContextSection">
            <div className="bcDescTopBar">
              <h3 className="emailAiPopupSectionTitle">Description</h3>
              {/* Social Media Buttons — moved to the top of the container */}
              {rowData && (
                <div className="bonusclub-social-buttons-container">
                  {rowData.lead_website && (
                    <a 
                      href={formatURL(rowData.lead_website, 'https://')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-website"
                      title="Website besuchen"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="2" y1="12" x2="22" y2="12"></line>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                      </svg>
                    </a>
                  )}
                  {rowData.lead_instagram && (
                    <a 
                      href={formatURL(rowData.lead_instagram, 'https://instagram.com/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-instagram"
                      title="Instagram Profil"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                      </svg>
                    </a>
                  )}
                  {rowData.lead_facebook && (
                    <a 
                      href={formatURL(rowData.lead_facebook, 'https://facebook.com/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-facebook"
                      title="Facebook Seite"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                      </svg>
                    </a>
                  )}
                  {rowData.lead_linkedin && (
                    <a 
                      href={formatURL(rowData.lead_linkedin, 'https://linkedin.com/in/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-linkedin"
                      title="LinkedIn Profil"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                        <rect x="2" y="9" width="4" height="12"></rect>
                        <circle cx="4" cy="4" r="2"></circle>
                      </svg>
                    </a>
                  )}
                </div>
              )}
            </div>
            {rowData && (() => {
              const leadFields = [
                { label: "Company", value: rowData.business_name, icon: "fa fa-building" },
                { label: "City", value: rowData.lead_city, icon: "fa fa-location-dot" },
              ].filter((f) => f.value);
              if (leadFields.length === 0) return null;
              return (
                <div className="bcLeadFields">
                  {leadFields.map((f) => (
                    <div key={f.label} className="bcLeadField" title={`${f.label}: ${f.value}`}>
                      <span className="bcLeadFieldIcon"><i className={f.icon} /></span>
                      <div className="bcLeadFieldText">
                        <span className="bcLeadFieldLabel">{f.label}</span>
                        <span className="bcLeadFieldValue">{f.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="emailAiPopupContextWrapper">
              <textarea
                className="emailAiPopupContextInput"
                placeholder="Füge Details über den Empfänger hinzu..."
                value={contextInfo}
                onChange={(e) => setContextInfo(e.target.value)}
              ></textarea>
            </div>
          </div>
          <ResearchInfoTabs contact={rowData} />
        </div>

        {/* RECHTE SPALTE: Subject, Body, Extra Buttons, Generate/Send */}
        <div className="emailAiPopupRight">
          {/* Absender-E-Mail-Auswahl */}
          <div className="emailAiPopupField">
            <h3 className="emailAiPopupSectionTitle">From</h3>
            <div className="emailAiPopupSenderEmailsContainer">
              {senderEmails.length > 0 ? (
                <div className="emailAiPopupSenderEmailsScroll">
                  {senderEmails.length > 1 && (
                    <button
                      className={`emailAiPopupSenderEmailButton emailAiPopupSenderRandomBtn ${randomSenderMode ? 'active' : ''}`}
                      onClick={() => {
                        const next = !randomSenderMode;
                        setRandomSenderMode(next);
                        if (next) {
                          const others = senderEmails.filter(e => e.email !== selectedSenderEmail);
                          const pick = others[Math.floor(Math.random() * others.length)];
                          setSelectedSenderEmail(pick.email);
                        }
                      }}
                      title={randomSenderMode ? "Random sender active — click to disable" : "Enable random sender rotation"}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 3h5v5" /><path d="M4 20 21 3" />
                        <path d="M21 16v5h-5" /><path d="M15 15l6 6" />
                        <path d="M4 4l5 5" />
                      </svg>
                      Random
                    </button>
                  )}
                  {senderEmails.map((item) => (
                    <button 
                      key={item.email}
                      className={`emailAiPopupSenderEmailButton ${!randomSenderMode && selectedSenderEmail === item.email ? 'active' : ''}`}
                      onClick={() => {
                        setRandomSenderMode(false);
                        setSelectedSenderEmail(item.email);
                      }}
                    >
                      {item.sender_name 
                        ? <><span style={{fontWeight: 500}}>{item.sender_name}</span><span style={{marginLeft: 8, opacity: 0.6}}>{item.email}</span></>
                        : item.email}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="emailAiPopupNoEmails">No sender emails configured.</p>
              )}
            </div>
          </div>

          {/* Empfänger-E-Mail-Auswahl */}
          <div className="emailAiPopupRecipientSection">
            <h3 className="emailAiPopupSectionTitle">To</h3>
            <div className="emailAiPopupTopButtons">
              {allEmails.map((entry, index) => {
                const undeliverable = isEmailUndeliverable(entry.email);
                const undeliverableType = undeliverable ? (bounceTypeOf(entry.email) || 'hard') : null;
                return (
                <div key={index} className="emailAiPopupButtonWrapper">
                  {entry.emailMissing ? (
                    <button
                      className="emailAiPopupButton dm-email-missing"
                      disabled
                      title={`Decision Maker: ${entry.dmName} – Email not found`}
                    >
                      <span style={{fontWeight: 600}}>{entry.dmName}</span>
                      {entry.dmRole && <span className="dm-role-badge">{entry.dmRole}</span>}
                      <span className="dm-email-missing-label">No email</span>
                    </button>
                  ) : (
                  <button
                    className={`emailAiPopupButton ${entry.email === selectedEmail ? 'active' : ''} ${isResendBlocked(entry.email) ? 'contacted' : ''} ${undeliverable ? 'undeliverable' : ''}`}
                    disabled={undeliverable}
                    onClick={() => {
                      if (undeliverable) return;
                      followUpRecipientTouchedRef.current = true;
                      if (ccEmails.includes(entry.email)) {
                        setCcEmails(ccEmails.filter(e => e !== entry.email));
                      }
                      setSelectedEmail(entry.email);
                    }}
                    title={undeliverable
                      ? (undeliverableType === 'soft'
                        ? 'Undeliverable: this address soft-bounced (temporary failure) on the last send — sending blocked'
                        : 'Undeliverable: this address hard-bounced on the last send — sending blocked')
                      : (isResendBlocked(entry.email)
                        ? 'Note: an email was already sent to this address'
                        : (entry.source === 'decision_maker' ? `Decision Maker: ${entry.dmName}` : 'General email'))}
                  >
                    <span style={undeliverable ? { textDecoration: 'line-through', opacity: 0.7 } : undefined}>
                      {entry.source === 'decision_maker' && entry.dmName
                        ? <><span style={{fontWeight: 600}}>{entry.dmName}</span>{entry.dmRole && <span className="dm-role-badge">{entry.dmRole}</span>}<span style={{marginLeft: 8, opacity: 0.6}}>{entry.email}</span></>
                        : entry.email}
                    </span>
                    {undeliverable && (
                      <span className="emailAiPopupUndeliverableBadge" title={`Undeliverable — ${undeliverableType === 'soft' ? 'Soft' : 'Hard'} bounce`}>
                        <i className="fa fa-times-circle"></i>
                      </span>
                    )}
                    {isResendBlocked(entry.email) && (
                      <span className="emailAiPopupContactedBadge" title="Bereits angeschrieben">
                        <i className="fa fa-exclamation-circle"></i>
                      </span>
                    )}
                    {entry.source === 'decision_maker' && (() => {
                      const eKey = entry.email.toLowerCase();
                      const override = dmEmailOverrides[eKey];
                      const isInconclusive = override?.inconclusive;
                      const isCheckedValid = entry.emailChecked || entry.emailVerified;
                      return (
                      <span
                        className={`dm-email-check-action ${isCheckedValid ? 'checked' : ''} ${isInconclusive ? 'inconclusive' : ''} ${checkingDmEmails[eKey] ? 'loading' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleVerifyDmEmail(entry);
                        }}
                        title={isCheckedValid ? 'Decision Maker email verified' : isInconclusive ? 'Verifizierung nicht möglich – der Mailserver blockiert die Prüfung. Bei info@-Adressen ist das normal.' : 'Verify this Decision Maker email'}
                      >
                        {checkingDmEmails[eKey] ? <i className="fa fa-spinner fa-spin"></i> : <i className={`fa ${isCheckedValid ? 'fa-check-circle' : isInconclusive ? 'fa-question-circle' : 'fa-search'}`}></i>}
                      </span>
                      );
                    })()}
                    {entry.email !== selectedEmail && (
                      <span
                        className={`emailAiPopupCcInline ${ccEmails.includes(entry.email) ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          toggleCcEmail(entry.email);
                        }}
                      >
                        {ccEmails.includes(entry.email) ? "CC ✓" : "CC"}
                      </span>
                    )}
                  </button>
                  )}
                </div>
                );
              })}
              {getDecisionMakersWithoutEmail(effectiveRowData).map((dm, i) => (
                <div key={`dm-noemail-${i}`} className="emailAiPopupButtonWrapper">
                  <span className="dm-info-badge-noemail">
                    <span style={{fontWeight: 600}}>{dm.name}</span>
                    {dm.role && <span className="dm-role-badge">{dm.role}</span>}
                  </span>
                </div>
              ))}
              {/* Inline Add Decision Maker */}
              {inlineAddDm ? (
                <div className="inline-add-dm-form">
                  <input
                    className="inline-add-dm-input"
                    placeholder="Name"
                    value={inlineAddDm.name}
                    onChange={(e) => setInlineAddDm(prev => ({ ...prev, name: e.target.value }))}
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveInlineDm()}
                  />
                  <input
                    className="inline-add-dm-input"
                    placeholder="Email"
                    type="email"
                    value={inlineAddDm.email}
                    onChange={(e) => setInlineAddDm(prev => ({ ...prev, email: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveInlineDm()}
                  />
                  <input
                    className="inline-add-dm-input inline-add-dm-role"
                    placeholder="Role"
                    value={inlineAddDm.role}
                    onChange={(e) => setInlineAddDm(prev => ({ ...prev, role: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveInlineDm()}
                  />
                  <button className="inline-add-dm-save" onClick={handleSaveInlineDm} disabled={savingDm}>
                    {savingDm ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-check"></i>}
                  </button>
                  <button className="inline-add-dm-cancel" onClick={() => setInlineAddDm(null)}>
                    <i className="fa fa-times"></i>
                  </button>
                </div>
              ) : (
                <button
                  className="inline-add-dm-btn"
                  onClick={(e) => {
                    setInlineAddDm({ name: '', email: '', role: '' });
                    const row = e.currentTarget.closest('.emailAiPopupTopButtons');
                    if (row) requestAnimationFrame(() => { row.scrollLeft = row.scrollWidth; });
                  }}
                  title="Add Decision Maker"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              )}
            </div>
          </div>

          {/* Betreff und Inhalt */}
          <div className="subject-regen-wrapper">
            <button
              type="button"
              className={`subject-regen-btn ${subjectAltsOpen ? 'active' : ''} ${subjectAltsLoading ? 'loading' : ''}`}
              onClick={handleRegenerateSubjectLines}
              title="Generate alternatives"
            >
              {subjectAltsLoading ? (
                <span className="subject-regen-spinner" />
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              )}
            </button>
            <input
              className={`emailAiPopupSubjectInput${!subjectLine && (isGenerating || subjectTemplateRef.current) ? ' subject-skeleton' : ''}`}
              placeholder={!subjectLine && subjectTemplateRef.current ? '' : 'Subject line'}
              value={subjectLine}
              onChange={(e) => {
                setSubjectLine(e.target.value);
                if (subjectTemplateRef.current) subjectTemplateRef.current = null;
              }}
              ref={subjectInputRef}
            />
            {subjectAltsOpen && (
              <div className="subject-alts-dropdown">
                {subjectAltsLoading ? (
                  <div className="subject-alts-loading">
                    <span className="subject-alts-loading-dot" />
                    <span className="subject-alts-loading-dot" />
                    <span className="subject-alts-loading-dot" />
                  </div>
                ) : subjectAlts.length > 0 ? (
                  subjectAlts.map((alt, i) => (
                    <button
                      key={i}
                      type="button"
                      className="subject-alt-item"
                      onClick={() => handleSelectSubjectAlt(alt)}
                    >
                      <span className="subject-alt-text">{alt}</span>
                      <span className="subject-alt-use">Use</span>
                    </button>
                  ))
                ) : (
                  <div className="subject-alts-empty">No alternatives generated</div>
                )}
              </div>
            )}
          </div>

      
          <div className={`emailAiPopupTexfieldwrapper ${isGenerating || isPerfecting ? 'loading' : ''}`} style={{position: 'relative'}}>
            <div className="salesgod-loading-container">
              <div className="sg-blob-extra"></div>
              <div className="sg-blob-extra"></div>
              <div className="sg-blob-extra"></div>
              <div className="sg-shimmer"></div>
              <img src="/img/salesgod_dark.webp" alt="BonusClub" className="salesgod-loading-logo" style={{width: '40px', height: '40px'}} />
              <div className="sg-loading-text">
                <span className="sg-loading-subtitle">{isPerfecting ? 'Perfecting your email...' : 'Creating your email...'}</span>
              </div>
            </div>
            <RichEmailEditor
              className="emailAiPopupTextArea"
              value={bodyContent}
              onChange={setBodyContent}
              images={aiImages}
              assets={userSettings?.imageLabAssets || {}}
              placeholder={isGenerating || isPerfecting ? "" : "Schreibe deine E-Mail hier..."}
              disabled={isGenerating || isPerfecting}
              onRegenerate={regenerateAiImage}
              onRemove={removeAiImage}
              onRefine={refineAiImage}
            />
          </div>
          {screenComposerImage && (
            <div className="sc-inline-preview">
              <img
                src={screenComposerImage.url}
                alt=""
                className="sc-inline-preview-img"
              />
              <button className="sc-inline-preview-remove" onClick={() => setScreenComposerImage(null)} title="Remove">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          )}
          <EmailAttachmentBar
            userId={userId}
            attachments={attachments}
            onChange={setAttachments}
            disabled={isGenerating || isPerfecting || isSending}
            fileInputRef={attachmentInputRef}
          />
          <div className="emailAiPopupBottomButtons">
            <div className="emailAiPopupActionButtons" ref={actionMenuRef}>
              <div className="cloneQuickWrap" ref={regenQuickRef}>
                <button
                  type="button"
                  className={`cloneQuickButton ${cloneQuickOpen ? 'active' : ''}`}
                  onClick={() => setCloneQuickOpen(o => !o)}
                  disabled={isGenerating || isPerfecting}
                  title="Re-Generate"
                >
                  <i className="fa-solid fa-arrows-rotate" />
                </button>
                {cloneQuickOpen && (
                  <div className={`cloneQuickPopover ${cloneRegenState.isCloneRegen && cloneRegenState.switchableClones.length > 0 ? 'isClones' : 'isStandard'}`}>
                    {cloneRegenState.isCloneRegen && cloneRegenState.switchableClones.length > 0 ? (
                      <>
                        <div className="cloneQuickTitle">
                          <i className="fa-solid fa-wand-magic-sparkles" />
                          <span>Re-Generate with CopyTwin</span>
                        </div>
                        <div className="cloneQuickRow">
                          {cloneRegenState.switchableClones.map(c => {
                            const isDefault = c.id === cloneRegenState.defaultCloneId;
                            const label = cloneRegenLabel(c);
                            const initial = (label.match(/[A-Za-z0-9]/) || ['•'])[0].toUpperCase();
                            return (
                              <button
                                key={c.id}
                                type="button"
                                className={`cloneQuickItem ${isDefault ? 'isDefault' : ''}`}
                                onClick={() => handleQuickCloneRegenerate(isDefault ? null : c.id)}
                                title={isDefault ? `${label} (template default)` : `Re-Generate with ${label}`}
                              >
                                <span className="cloneQuickAvatar">
                                  {c.avatar ? <img src={c.avatar} alt="" /> : <span>{initial}</span>}
                                </span>
                                <span className="cloneQuickName">{label}</span>
                                {isDefault && <span className="cloneQuickDot" />}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="cloneQuickStandard">
                        <div className="framework-picker-custom-note">
                          <textarea
                            className="framework-picker-custom-textarea"
                            placeholder="Spezielle Anweisungen (optional)..."
                            value={regenCustomNote}
                            onChange={(e) => setRegenCustomNote(e.target.value)}
                            rows={2}
                            onMouseDown={(e) => e.stopPropagation()}
                          />
                        </div>
                        <button
                          className="framework-picker-standard"
                          onClick={() => { setCloneQuickOpen(false); handleGenerateAndClose(); }}
                        >
                          <i className="fa-solid fa-refresh" />
                          <span>Standard Re-Generate</span>
                        </button>
                        <div className="framework-picker-divider" />
                        {emailPersonalities.map(p => (
                          <div key={p.id} className="framework-picker-personality">
                            <button
                              className={`framework-picker-personality-btn ${expandedPersonality === p.id ? 'active' : ''}`}
                              onClick={() => setExpandedPersonality(expandedPersonality === p.id ? null : p.id)}
                            >
                              <img src={p.image} alt={p.name} className="framework-picker-avatar" />
                              <div className="framework-picker-personality-info">
                                <span className="framework-picker-personality-name">{p.name}</span>
                                <span className="framework-picker-personality-tagline">{p.tagline}</span>
                              </div>
                              <i className={`fa-solid fa-chevron-${expandedPersonality === p.id ? 'up' : 'down'}`} style={{fontSize: '9px', color: 'var(--blue, #6897B7)', opacity: 0.5}} />
                            </button>
                            {expandedPersonality === p.id && (
                              <div className="framework-picker-frameworks">
                                {p.frameworks.map(fw => (
                                  <button
                                    key={fw.id}
                                    className="framework-picker-fw-btn"
                                    onClick={() => { setCloneQuickOpen(false); handleFrameworkRegenerate(p.id, fw.id); }}
                                  >
                                    <div className="framework-picker-fw-icon">
                                      <i className={`fa-solid ${fw.icon}`} />
                                    </div>
                                    <div className="framework-picker-fw-info">
                                      <span className="framework-picker-fw-name">{fw.name}</span>
                                      <span className="framework-picker-fw-short">{fw.short}</span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button 
                className={`emailAiPopupActionMenuButton ${isActionMenuOpen ? 'active' : ''}`}
                onClick={toggleActionMenu}
                disabled={isGenerating || isPerfecting}
                title="AI Actions"
              >
                <img src="/img/menu_1.svg" alt="AI Actions" className="emailAiPopupActionMenuIcon" />
              </button>
              
                              {isActionMenuOpen && (
                  <div className="emailAiPopupActionMenu">
                    {userSettings.calendlyBookingLink && (
                      <button 
                        className="emailAiPopupActionMenuItem"
                        onClick={handleCopyCalendlyLink}
                      >
                        <div className="emailAiPopupActionMenuItemIconWrapper">
                          <img src="/img/copy.svg" alt="Copy Link" className="emailAiPopupActionMenuItemIcon" />
                        </div>
                        <span>Copy Calendly Link</span>
                      </button>
                    )}
                    {userSettings.calendlyBookingLink && (
                      <button 
                        className="emailAiPopupActionMenuItem"
                        onClick={handleOpenCalendly}
                      >
                        <div className="emailAiPopupActionMenuItemIconWrapper">
                          <img src="/img/calendly.svg" alt="Calendly" className="emailAiPopupActionMenuItemIcon" />
                        </div>
                        <span>Open Calendly</span>
                      </button>
                    )}
                    <button 
                      className="emailAiPopupActionMenuItem"
                      onClick={handlePerfectAndClose}
                      disabled={!bodyContent || bodyContent.trim() === ""}
                    >
                      <div className="emailAiPopupActionMenuItemIconWrapper">
                        <img src="/img/magic-wand.svg" alt="Perfect" className="emailAiPopupActionMenuItemIcon" />
                      </div>
                      <span>Make Perfect <span className="aiLabel">AI</span></span>
                    </button>
                    <button 
                      className="emailAiPopupActionMenuItem"
                      onClick={() => { setFrameworkPickerOpen(!frameworkPickerOpen); }}
                    >
                      <div className="emailAiPopupActionMenuItemIconWrapper">
                        <img src="/img/refresh-arrow.svg" alt="Generate" className="emailAiPopupActionMenuItemIcon" />
                      </div>
                      <span>Re-Generate <span className="aiLabel">AI</span></span>
                      <i className={`fa-solid fa-chevron-${frameworkPickerOpen ? 'up' : 'right'}`} style={{marginLeft: 'auto', fontSize: '9px', color: 'var(--blue, #6897B7)', opacity: 0.5}} />
                    </button>
                    {frameworkPickerOpen && (() => {
                      // CopyTwin (voice-clone) templates offer a one-off clone
                      // switch instead of the standard writer personas (which
                      // don't apply to voice-clone generation).
                      const { isCloneRegen, defaultCloneId, switchableClones } = cloneRegenState;
                      const cloneLabel = cloneRegenLabel;
                      // Hide the divider when nothing follows it (CopyTwin with a single clone).
                      const showAfterDivider = isCloneRegen ? switchableClones.length > 1 : true;
                      return (
                      <div className="framework-picker-panel">
                        <div className="framework-picker-custom-note">
                          <textarea
                            className="framework-picker-custom-textarea"
                            placeholder="Spezielle Anweisungen (optional)..."
                            value={regenCustomNote}
                            onChange={(e) => setRegenCustomNote(e.target.value)}
                            rows={2}
                            onMouseDown={(e) => e.stopPropagation()}
                          />
                        </div>
                        <button 
                          className="framework-picker-standard"
                          onClick={handleGenerateAndClose}
                        >
                          <i className="fa-solid fa-refresh" />
                          <span>Standard Re-Generate</span>
                        </button>
                        {showAfterDivider && <div className="framework-picker-divider" />}
                        {isCloneRegen ? (
                          switchableClones.length > 1 ? (
                            <div className="cloneSwitch">
                              <div className="cloneSwitchLabel">
                                <i className="fa-solid fa-wand-magic-sparkles" />
                                <span>Generate with another CopyTwin</span>
                              </div>
                              <div className="cloneSwitchList">
                                {switchableClones.map(c => {
                                  const isDefault = c.id === defaultCloneId;
                                  const label = cloneLabel(c);
                                  const initial = (label.match(/[A-Za-z0-9]/) || ['•'])[0].toUpperCase();
                                  return (
                                    <button
                                      key={c.id}
                                      className={`cloneSwitchItem ${isDefault ? 'isDefault' : ''}`}
                                      onClick={() => handleCloneRegenerate(isDefault ? null : c.id)}
                                      title={isDefault ? `${label} (template default)` : `Re-Generate with ${label}`}
                                    >
                                      <span className="cloneSwitchAvatar">
                                        {c.avatar
                                          ? <img src={c.avatar} alt="" />
                                          : <span>{initial}</span>}
                                      </span>
                                      <span className="cloneSwitchName">{label}</span>
                                      {isDefault
                                        ? <span className="cloneSwitchBadge">Default</span>
                                        : <i className="fa-solid fa-arrow-right cloneSwitchGo" />}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ) : null
                        ) : (
                          emailPersonalities.map(p => (
                          <div key={p.id} className="framework-picker-personality">
                            <button 
                              className={`framework-picker-personality-btn ${expandedPersonality === p.id ? 'active' : ''}`}
                              onClick={() => setExpandedPersonality(expandedPersonality === p.id ? null : p.id)}
                            >
                              <img src={p.image} alt={p.name} className="framework-picker-avatar" />
                              <div className="framework-picker-personality-info">
                                <span className="framework-picker-personality-name">{p.name}</span>
                                <span className="framework-picker-personality-tagline">{p.tagline}</span>
                              </div>
                              <i className={`fa-solid fa-chevron-${expandedPersonality === p.id ? 'up' : 'down'}`} style={{fontSize: '9px', color: 'var(--blue, #6897B7)', opacity: 0.5}} />
                            </button>
                            {expandedPersonality === p.id && (
                              <div className="framework-picker-frameworks">
                                {p.frameworks.map(fw => (
                                  <button 
                                    key={fw.id}
                                    className="framework-picker-fw-btn"
                                    onClick={() => handleFrameworkRegenerate(p.id, fw.id)}
                                  >
                                    <div className="framework-picker-fw-icon">
                                      <i className={`fa-solid ${fw.icon}`} />
                                    </div>
                                    <div className="framework-picker-fw-info">
                                      <span className="framework-picker-fw-name">{fw.name}</span>
                                      <span className="framework-picker-fw-short">{fw.short}</span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          ))
                        )}
                      </div>
                      );
                    })()}
                    <div style={{height: '1px', background: '#e5e7eb', margin: '4px 8px'}}></div>
                    <button 
                      className="emailAiPopupActionMenuItem"
                      onClick={() => { setShowScreenComposer(true); setIsActionMenuOpen(false); }}
                    >
                      <div className="emailAiPopupActionMenuItemIconWrapper">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <rect x="2" y="3" width="20" height="14" rx="2"/>
                          <path d="M8 21H16M12 17V21"/>
                        </svg>
                      </div>
                      <span>Add Image/Gif</span>
                    </button>
                    <button
                      className="emailAiPopupActionMenuItem"
                      onClick={() => {
                        setIsActionMenuOpen(false);
                        if (attachmentInputRef.current) attachmentInputRef.current.click();
                      }}
                    >
                      <div className="emailAiPopupActionMenuItemIconWrapper">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                        </svg>
                      </div>
                      <span>Attach File</span>
                    </button>
                  </div>
                )}
              <EmailHistoryPanel
                userId={userId}
                contactId={rowData?.id}
                recipientEmails={emailAddresses}
              />
            </div>
            <ScreenComposerNotifier
              isGenerating={scBackground.isGenerating}
              progress={scBackground.progress}
              error={scBackground.error}
              justCompleted={scBackground.justCompleted}
              onDismiss={scBackground.dismiss}
              onCancel={scBackground.cancel}
            />
            {followUpDuplicates.length > 0 && (
              <FollowUpHistoryWarning
                sentCount={followUpDuplicates.length}
                lastSent={followUpDuplicates[0]}
              />
            )}
            <CallContextChip contact={rowData} topic={selectedTopicData} />
            <LiveSpamBadge
              body={bodyContent}
              subject={subjectLine}
              mode={spamCheckMode}
              aiResult={aiSpam}
              aiPending={aiSpamPending}
              isFollowUp={isFollowUpStep}
            />
            <button
              className={`emailAiPopupSendButton ${sendSuccess ? 'success' : ''} ${isSending ? 'sending' : ''} ${!isEmailValid() || isGeneratingImages ? 'disabled' : ''}`}
              onClick={handleSend}
              disabled={isGenerating || isSending || !isEmailValid() || isPerfecting || isGeneratingImages}
              title={isGeneratingImages ? "Waiting for the AI image to finish generating…" : undefined}
            >
              {isSending ? (
                <>
                  <div className="emailAiPopupSpinner"></div>
                  <span>Sending...</span>
                </>
              ) : isGeneratingImages ? (
                <>
                  <div className="emailAiPopupSpinner"></div>
                  <span>Generating image…</span>
                </>
              ) : sendSuccess ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Sent</span>
                </>
              ) : (
                <>
                  <span>Send Email</span>
                  <img src="/img/sender.svg" alt="Send" className="emailAiPopupSendIcon" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Perfect Slider Popup */}
      {perfectSliderOpen && (
        <div className="perfect-slider-overlay" onClick={() => setPerfectSliderOpen(false)}>
          <div className="perfect-slider-popup" onClick={e => e.stopPropagation()}>
            <div className="perfect-slider-header">
              <svg className="perfect-slider-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.09 8.26L18 6L14.74 10.91L21 12L14.74 13.09L18 18L13.09 15.74L12 22L10.91 15.74L6 18L9.26 13.09L3 12L9.26 10.91L6 6L10.91 8.26L12 2Z" fill="url(#sparkleGrad)" stroke="url(#sparkleGrad)" strokeWidth="0.5" strokeLinejoin="round"/>
                <defs><linearGradient id="sparkleGrad" x1="3" y1="2" x2="21" y2="22" gradientUnits="userSpaceOnUse"><stop stopColor="#6897B7"/><stop offset="1" stopColor="#2a6776"/></linearGradient></defs>
              </svg>
              <h3>Make Perfect</h3>
            </div>
            <p className="perfect-slider-desc">
              {perfectCreativity <= 2 ? 'Fix typos & grammar only — keep text almost identical'
                : perfectCreativity <= 4 ? 'Polish wording & improve clarity — same structure'
                : perfectCreativity <= 6 ? 'Rephrase & enhance — noticeable improvements'
                : perfectCreativity <= 8 ? 'Substantial rewrite — fresh phrasing, same intent'
                : 'Completely new email — new angle, new structure'}
            </p>
            <div className="perfect-slider-track">
              <input
                type="range"
                min="1"
                max="10"
                value={perfectCreativity}
                onChange={e => setPerfectCreativity(Number(e.target.value))}
                className="perfect-slider-input"
              />
              <div className="perfect-slider-steps">
                {[
                  { val: 1, label: 'Fix' },
                  { val: 3, label: 'Polish' },
                  { val: 5, label: 'Enhance' },
                  { val: 7, label: 'Rewrite' },
                  { val: 10, label: 'New' },
                ].map(s => (
                  <span
                    key={s.val}
                    className={`perfect-slider-step ${perfectCreativity >= s.val ? 'active' : ''}`}
                    onClick={() => setPerfectCreativity(s.val)}
                  >{s.label}</span>
                ))}
              </div>
            </div>
            <div className="perfect-slider-actions">
              <button className="perfect-slider-cancel" onClick={() => setPerfectSliderOpen(false)}>Cancel</button>
              <button className="perfect-slider-go" onClick={() => { setPerfectSliderOpen(false); handlePerfectEmail(perfectCreativity); }}>
                GO
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    <button className="side-panel-toggle" disabled={isGenerating} onClick={() => setSidePanelOpen(prev => !prev)} title={sidePanelOpen ? 'Close panel' : 'Open panel'}>
      <svg width="7" height="12" viewBox="0 0 7 12" fill="none"><path d={sidePanelOpen ? "M5.5 1L1 6L5.5 11" : "M1.5 1L6 6L1.5 11"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
    </button>
    <div className="emailAiSidePanel">
      <div className="side-panel-content">
        <div className="sparks-header">
          <span className="sparks-title">SalesGod Writer</span>
          <button className="sparks-refresh" onClick={() => { setActiveDirection(null); setActiveCategory(null); fetchWritingSparks(bodyContent); }} disabled={sparksLoading} title="Neu generieren (frische Angles)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
          </button>
        </div>
        <div className="sparks-actions-bar">
          {writerDirections.length > 0 && writerDirections.slice(0, 4).map((dir) => (
            <button
              key={dir.prompt}
              className={`sparks-direction-btn ${activeDirection === dir.prompt ? 'active' : ''} ${sparksLoading ? 'sparks-btn-loading' : ''}`}
              onClick={() => {
                setActiveDirection(dir.prompt);
                setActiveCategory(null);
                fetchWritingSparks(bodyContent, dir.prompt);
              }}
            >
              {dir.label}
            </button>
          ))}
          {writerDirections.length > 0 && (
            <button className="sparks-more-toggle" onClick={() => setSidePanelShowMore(p => !p)}>
              {sidePanelShowMore ? '− less' : '+ more'}
            </button>
          )}
          {sidePanelShowMore && (
            <div className="sparks-categories-row">
              {WRITER_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  className={`sparks-category-btn ${activeCategory === cat.id ? 'active' : ''} ${sparksLoading ? 'sparks-btn-loading' : ''}`}
                  onClick={() => { setActiveCategory(cat.id); setActiveDirection(null); fetchWritingSparks(bodyContent, null, cat.id); }}
                  title={cat.label}
                >
                  <span className="sparks-category-icon">{WRITER_CATEGORY_ICONS[cat.id]}</span>
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {sparksCopied === '__expand_failed__' && (
          <div className="sparks-expand-error">Expand fehlgeschlagen — nochmal versuchen</div>
        )}
        {sparksLoading && writingSparks.length === 0 ? (
          <div className="sparks-loading">
            {[...Array(8)].map((_, i) => <div key={i} className="spark-skeleton" />)}
          </div>
        ) : writingSparks.length > 0 ? (
          <div className="sparks-list">
            {sparksLoadingPos === 'top' && (
              <div className="sparks-inline-loading">
                {[...Array(3)].map((_, i) => <div key={`skel-top-${i}`} className="spark-skeleton spark-skeleton-inline" />)}
              </div>
            )}
            {writingSparks.map((spark, i) => {
              const sparkKey = `${i}-${spark.text?.substring(0, 20)}`;
              const prevSpark = writingSparks[i - 1];
              const isFirstPrevious = spark.previous && (i === 0 || !prevSpark?.previous);
              const isFirstIcebreaker = spark.type === 'icebreaker' && !spark.previous && (i === 0 || prevSpark?.type !== 'icebreaker' || prevSpark?.previous);
              const isFirstBatch1 = spark.batch === 1 && spark.type !== 'icebreaker' && !spark.previous && !spark.expanded && !spark.directionLabel && (i === 0 || prevSpark?.type === 'icebreaker' || prevSpark?.batch !== 1 || prevSpark?.previous);
              const isFirstBatch2 = spark.batch === 2 && !spark.previous && !spark.expanded && !spark.directionLabel && prevSpark?.batch !== 2;
              const showBatchLabel = !activeDirection && (isFirstIcebreaker || isFirstBatch1 || isFirstBatch2);
              const batchLabelText = isFirstIcebreaker ? 'Icebreaker' : (spark.batch === 2 ? 'Fresh Angles' : 'Direct Impact');
              return (
                <React.Fragment key={sparkKey}>
                  {isFirstPrevious && (
                    <div className="sparks-previous-divider">
                      <span>Vorherige Vorschläge</span>
                    </div>
                  )}
                  {showBatchLabel && (
                    <div className={`sparks-batch-label${isFirstIcebreaker ? ' sparks-batch-label-icebreaker' : ''}`}>{batchLabelText}</div>
                  )}
                  <button
                    className={`spark-card ${sparksCopied === spark.text ? 'copied' : ''} ${spark.expanded ? 'spark-expanded' : ''} ${expandingIdx === i ? 'spark-expanding' : ''} ${spark.previous ? 'spark-previous' : ''} ${spark.type === 'icebreaker' ? 'spark-icebreaker' : ''}`}
                    data-color-idx={spark.colorIdx ?? (i % 4)}
                    onClick={() => {
                      navigator.clipboard.writeText(spark.text).catch(() => {});
                      setSparksCopied(spark.text);
                      setTimeout(() => setSparksCopied(null), 1500);
                    }}
                    title="Click to copy"
                  >
                    <span className="spark-text">{spark.text}</span>
                    <span className="spark-card-footer">
                      {sparksCopied === spark.text ? (
                        <span className="spark-badge" style={{ color: '#16a34a' }}>Copied!</span>
                      ) : (() => {
                        const assignedPersona = activePersona
                          ? SALESGOD_PERSONAS.find(p => p.id === activePersona)
                          : SALESGOD_PERSONAS[i % SALESGOD_PERSONAS.length];
                        return assignedPersona ? (
                          <span
                            className={`spark-persona-tag ${activePersona === assignedPersona.id ? 'active' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              const newP = activePersona === assignedPersona.id ? null : assignedPersona.id;
                              setActivePersona(newP);
                              setActiveDirection(null);
                              setActiveCategory(null);
                              // Keep the digest (lean) but reset the ledger so the new voice
                              // regenerates across the full angle pool.
                              usedAnglesRef.current = [];
                              fetchWritingSparks(bodyContent);
                            }}
                            title={activePersona === assignedPersona.id ? 'Persona deaktivieren' : `Mit ${assignedPersona.name}'s Stil generieren`}
                          >
                            <img src={assignedPersona.image} alt="" className="spark-persona-mini" />
                            <span>{assignedPersona.name}</span>
                          </span>
                        ) : (
                          <span className="spark-badge">{spark.type === 'icebreaker' ? (spark.style || 'icebreaker') : (spark.beat || (spark.batch === 2 ? 'fresh' : 'direct'))}</span>
                        );
                      })()}
                      <span
                        className="spark-expand-btn"
                        onClick={(e) => { e.stopPropagation(); expandSpark(i); }}
                        title="Variationen generieren"
                      >
                        {expandingIdx === i ? (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="10"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></circle></svg>
                        ) : (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                        )}
                      </span>
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
            {sparksLoadingPos === 'bottom' && (
              <div className="sparks-inline-loading">
                {[...Array(3)].map((_, i) => <div key={`skel-bot-${i}`} className="spark-skeleton spark-skeleton-inline" />)}
              </div>
            )}
            <button
              className="sparks-load-more-btn"
              onClick={() => fetchWritingSparks(bodyContent, activeDirection || undefined, activeCategory || undefined, 'bottom')}
              disabled={sparksLoading}
            >
              {sparksLoading ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="10"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/></circle></svg>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  <span>Create 5 more</span>
                </>
              )}
            </button>
          </div>
        ) : (
          <p className="sparks-empty">Sparks werden geladen sobald die Email generiert ist.</p>
        )}
      </div>
    </div>
    {showScreenComposer && (
      <ScreenComposerPopup
        onClose={() => setShowScreenComposer(false)}
        recipientUrl={rowData?.lead_website || ''}
        onInsert={(imageData) => {
          setScreenComposerImage(imageData);
          setShowScreenComposer(false);
        }}
        onGenerateBackground={(params) => {
          scBackground.startGeneration(params);
        }}
      />
    )}
    <SendConfirmModal
      isOpen={resendConfirmOpen}
      variant="danger"
      recipient={selectedEmail}
      sentCount={sentCount}
      lastSent={lastSent}
      sentMessages={sentMessages}
      title="Address already emailed"
      message={`An intro or follow-up has already been sent to ${selectedEmail}. Are you sure you want to email this exact address again?`}
      confirmText="Send anyway"
      cancelText="Cancel"
      onCancel={() => setResendConfirmOpen(false)}
      onConfirm={() => {
        setResendConfirmOpen(false);
        resendConfirmedRef.current = true;
        handleSend();
      }}
    />
    <SendConfirmModal
      isOpen={followUpConfirmOpen}
      variant="warning"
      recipient={selectedEmail}
      sentCount={followUpDuplicates.length}
      lastSent={followUpDuplicates[0] || null}
      sentMessages={followUpDuplicates}
      title="This follow-up was already sent"
      message={`This follow-up step was already sent to ${selectedEmail}. Send the same step again?`}
      confirmText="Send follow-up"
      cancelText="Cancel"
      onCancel={() => setFollowUpConfirmOpen(false)}
      onConfirm={() => {
        setFollowUpConfirmOpen(false);
        followUpConfirmedRef.current = true;
        handleSend();
      }}
    />
    </div>
  );
};

export default NormalModeEmail; 